/**
 * Rendu de la ville avec PixiJS v8.
 *
 * Rôle volontairement étroit : instancier l'application Pixi, poser un sprite par case
 * dans le bon ordre, et reporter l'état de la caméra sur le conteneur monde à chaque frame.
 * Toute la géométrie vient d'`isoEngine`, tout le comportement d'entrée vient de `camera` :
 * ce fichier ne décide de rien.
 */

/*
  ⚠️ CE PREMIER IMPORT DOIT RESTER LE PREMIER — c'est lui qui rend la CSP possible.

  PixiJS v8 GÉNÈRE du code à l'exécution pour synchroniser ses uniformes et ses shaders,
  via `new Function()`. Sous une politique de contenu sans `'unsafe-eval'`, son
  initialisation lève « Current environment does not allow unsafe-eval » et **le jeu ne
  démarre pas du tout** — page noire, aucun autre symptôme.

  Trouvé par `npm run probe:csp`, pas par une relecture : une CSP trop stricte ne produit
  aucune erreur au build. C'est exactement le livrable que la règle 11 vise.

  L'alternative était d'autoriser `'unsafe-eval'` dans `script-src`, ce qui aurait vidé la
  politique de l'essentiel de son intérêt : `eval` est le vecteur que la CSP existe pour
  fermer. Pixi fournit ce module précisément pour ce cas ; il remplace la génération de
  code par des fonctions écrites à la main, à un coût mesuré au § « performance » du
  CHANGELOG.

  Importé ICI et non dans `main.ts` : `pages/ville.ts` instancie le même renderer, et
  deux points d'installation à tenir synchronisés en oublieraient un.
*/
import 'pixi.js/unsafe-eval';

import { Application, Container, Graphics, Sprite, Texture, TextureSource } from 'pixi.js';
import type { Renderer } from 'pixi.js';

import { CAMERA_BALANCE } from '@/config/balance';
import { buildingByType } from '@/config/buildings';
import { isLandmark } from '@/config/landmarks';
import { traceSpriteFor } from '@/config/decorations';
import type { BuildingDefinition } from '@/config/buildings';
import type { PlacedBuilding } from '@/core/gameState';
import { TweenRunner } from '@/core/tween';

/**
 * Amplitude du sursaut d'un bâtiment qui paie, en fraction de sa hauteur.
 *
 * 5 % — mesuré à l'œil sur la ville de démonstration, puis rentré : à 12 % le bâtiment
 * « saute » et l'attention quitte la ville pour le mouvement ; à 2 % on ne le voit pas.
 * En projection isométrique, une variation verticale se lit plus fort qu'elle n'est,
 * parce que la silhouette est haute et le sol plat.
 */
const BUMP_AMOUNT = 0.05;
import type { ViewportInsets } from './camera';
import { Camera, screenToWorld, worldToScreen } from './camera';
import {
  cellCount,
  cellToWorld,
  createGrid,
  depthOf,
  forEachCellInDrawOrder,
  gridWorldBounds,
  isInsideGrid,
  resizeGrid,
  worldToCell,
} from './isoEngine';
import type { GridCell, IsoGrid } from './isoEngine';
import { JUICE } from '@/config/juice';
import { footprintOf, orientationFor, variantFor } from '@/core/gameState';
import { Celebrations } from './celebrations';
import { CoinFlight, rainCount } from './coinFlight';
import type { ScreenPoint } from './coinFlight';
import { FloatingNumbers } from './floatingNumbers';
import { playPlacementJuice } from './placementJuice';
import { connectionMask, parseRoadKey, roadSprite } from '@/core/roads';
import { controlFor, greenAxis, minorBranchOf } from '@/core/traffic';
import { DAY_LENGTH_MS, skyAt, urbanLightAt, windowGlowAt, windowLitGate } from '@/core/dayCycle';
import { LAMP_HEADS, TRAFFIC_BULBS } from './lampHeads.generated';
import { Sky } from './sky';
import { Traffic } from './traffic';
import { QualityManager } from './quality';
import type { QualityLevel } from './quality';
import { isGone, whileAlive } from './spriteGuard';
import { assetScale } from './assetScale';
import { normalizeFacing, orientedSprite } from './facing';
import { SYNTY_TILE, buildingAnchorY, halfHeight, halfWidth } from './tileGeometry';
import { cityTexture } from './cityAtlas';

/**
 * Pas d'une case vers chacun des quatre côtés, dans l'ordre nord, est, sud, ouest.
 * Même ordre que `roadSide` : les deux tables se lisent ensemble ou pas du tout.
 */
const SIDE_STEP: readonly (readonly [number, number])[] = [
  [0, -1],
  [1, 0],
  [0, 1],
  [-1, 0],
];

/**
 * Pas d'une case vers chacune des quatre DIAGONALES, dans l'ordre nord-ouest, nord-est,
 * sud-est, sud-ouest.
 *
 * Sert à poser la signalisation : un carrefour à quatre branches a ses quatre voisins
 * orthogonaux occupés par de la chaussée, donc son feu ne peut vivre qu'au coin.
 */
const DIAGONAL_STEP: readonly (readonly [number, number])[] = [
  [-1, -1],
  [1, -1],
  [1, 1],
  [-1, 1],
];

/**
 * Cadence des lampadaires, en cases le long d'une RUE.
 *
 * Trois cases — quinze mètres — et un seul côté. La valeur précédente était deux, mais
 * elle comptait les cases riveraines sans distinguer les deux bords : une avenue
 * rectiligne recevait donc un mât tous les deux pas **de chaque côté**, soit un tous les
 * dix mètres en quinconce. Mesuré sur le réseau de test : 27 lampadaires pour 21 cases de
 * route. Ce n'est pas un éclairage public, c'est une forêt de poteaux.
 *
 * La cadence porte désormais sur la case de RUE, qui nomme un seul riverain (`lampAt`).
 */
const LAMP_SPACING = 3;

/**
 * Poussée du mobilier urbain vers le bord de SA tuile de route — **dérivée**.
 *
 * Le mobilier vit désormais SUR la case de route (demande de Benoit : « collé à la
 * route et que ça reste ») : la poussée se mesure donc depuis le centre de la rue,
 * vers l'extérieur, en RESTANT dans la tuile (< 0,5 — c'est ce qui le rend permanent,
 * une route ne se construisant jamais).
 *
 *     bord de chaussée      0,19 case  (mesuré, `npm run measure:roads`)
 *   + demi-largeur du banc  0,16 case  (1,6 m déclaré dans `assetScale`)
 *   + berme                 0,05 case
 *   ────────────────────────────────
 *     centre du mobilier    0,40 case du centre de la rue
 *
 * Un premier jet visait le point et non l'objet : placer un objet, ce n'est pas
 * placer un point — la demi-largeur du meuble fait partie de la somme.
 */
const FURNITURE_EDGE = 0.4;

/**
 * Les MÂTS (lampadaires) sont fins : demi-largeur ~0,06 case au pied. Ils se
 * collent donc plus près de la chaussée que les bancs — 0,19 + 0,06 + 0,05 = 0,30.
 * « Lampadaire toujours trop loin de la route » (Benoit) : la distance d'un objet
 * dépend de sa largeur, une seule constante ne peut pas servir aux deux.
 */
const LAMP_EDGE = 0.3;

/**
 * Poussée de la signalisation vers le COIN de la case du carrefour, en diagonale.
 * À (0,42 · 0,42), le poteau dégage les deux chaussées du croisement (± 0,19 sur
 * chaque axe) tout en restant sur sa tuile (coin à 0,5).
 */
const SIGNAL_CORNER = 0.42;

/**
 * Décalage de repli quand AUCUN coin n'est libre (ROADMAP 5.3).
 *
 * Au cœur d'un quartier dense, les quatre diagonales d'un carrefour sont elles-mêmes
 * des routes. Le placement préférait alors ne rien poser — mesuré sur un pavé de 5 × 5 :
 * **neuf carrefours, zéro panneau**, alors que `core/traffic.ts` applique toujours sa
 * règle. Les véhicules s'arrêtaient devant rien.
 *
 * On rentre donc le poteau vers le centre de sa propre case au lieu de renoncer : il
 * reste sur son carrefour sans surplomber la chaussée voisine. Un panneau un peu moins
 * bien placé vaut infiniment mieux qu'une règle invisible.
 */
const SIGNAL_CORNER_TIGHT = 0.24;

const GRASS_TILE_NAME = 'tile-grass';

/**
 * Sous-face rocheuse d'île flottante, posée sous les cases de BORDURE.
 *
 * Les dalles Synty ne font que 34 px d'épaisseur : le plateau paraissait découpé dans
 * du papier et flottait sur du vide. Cette sous-face lui rend son volume — et c'est un
 * asset du pack, pas un dégradé simulé, donc elle s'accorde exactement au reste.
 *
 * Bordure seulement : la sous-face d'une case intérieure est masquée par ses voisines,
 * la dessiner coûterait 64 sprites sur 100 pour zéro pixel visible.
 */
const ISLAND_SKIRT_NAME = 'tile-island';

/** Fond nuit, accordé au `--color-bg` de la feuille de style. */
const BACKGROUND_COLOR = 0x0f1724;

/** Écart entre le toit d'un bâtiment et sa pastille de palier, en pixels d'ÉCRAN (3.4). */
const PIP_SCREEN_GAP = 9;

/**
 * Zoom de départ, déduit de la largeur de tuile voulue à l'écran (§19 : le réglage vit
 * dans `balance.ts`, la logique s'y adapte). Volontairement indépendant du viewport.
 */
function initialZoom(): number {
  return CAMERA_BALANCE.initialTileScreenWidth / SYNTY_TILE.width;
}

/** Butées de zoom, exprimées en largeur de tuile à l'écran puis converties. */
function absoluteZoomLimits(): { min: number; max: number } {
  return {
    min: CAMERA_BALANCE.minTileScreenWidth / SYNTY_TILE.width,
    max: CAMERA_BALANCE.maxTileScreenWidth / SYNTY_TILE.width,
  };
}

export interface CityRendererOptions {
  readonly canvas: HTMLCanvasElement;
  /**
   * Élément qui donne sa taille au rendu.
   *
   * **Surtout pas le canvas lui-même.** Avec `autoDensity`, PixiJS écrit un style inline en
   * pixels fixes sur le canvas ; le mesurer reviendrait à lire la taille qu'on vient de lui
   * imposer, et il ne pourrait plus jamais grandir. Le canvas resterait figé sur la toute
   * première mesure, laissant une bande vide dès que la fenêtre s'agrandit.
   */
  readonly container: HTMLElement;
  readonly grid: IsoGrid;
}

export interface RendererStatus {
  readonly grid: IsoGrid;
  readonly zoom: number;
  readonly fps: number;
  readonly tiles: number;
  readonly quality: QualityLevel;
}

/**
 * Combien des quatre voisins orthogonaux d'une case portent un bâtiment.
 *
 * Sert à décider si une case libre est INTÉRIEURE à un quartier (donc un jardin) ou
 * perdue dans la campagne (donc un pré). On ne compte que les quatre voisins droits, pas
 * les diagonales : deux bâtiments en diagonale ne ferment pas un vide, ils laissent
 * passer le regard — et une case ainsi remplie donnerait une ville en damier.
 */
function countBuiltNeighbours(built: ReadonlySet<string>, cell: GridCell): number {
  let count = 0;
  if (built.has(`${String(cell.col)}:${String(cell.row - 1)}`)) count += 1;
  if (built.has(`${String(cell.col)}:${String(cell.row + 1)}`)) count += 1;
  if (built.has(`${String(cell.col - 1)}:${String(cell.row)}`)) count += 1;
  if (built.has(`${String(cell.col + 1)}:${String(cell.row)}`)) count += 1;
  return count;
}

export class CityRenderer {
  private readonly app = new Application();
  private readonly world = new Container();
  private readonly tileLayer = new Container();

  /*
    ══ LES DEUX REPÈRES DE POSE ═══════════════════════════════════════════════

    Ils ne vivent pas au même endroit, et c'est délibéré.

    **La grille** est un calque À PART, au-dessus de tout le monde trié. C'est ce que
    fait Clash of Clans, et pour une raison géométrique : dans une scène isométrique où
    une tuile de premier plan doit pouvoir masquer un bâtiment de derrière, il n'existe
    AUCUNE profondeur unique qui placerait un quadrillage entier « au-dessus du sol et
    sous les bâtiments ». Le poser au-dessus de tout est la seule position cohérente —
    et à 12 % d'opacité, il se lit comme un repère, pas comme un voile.

    **L'empreinte**, elle, appartient à une case précise : elle vit donc dans le calque
    trié, à `profondeur × 2 + 0,5` — au-dessus de la tuile de sa case (× 2), sous
    l'ombre (+ 0,75) et sous le fantôme (+ 1). Elle glisse ainsi correctement derrière
    les bâtiments situés devant elle, ce qu'un calque séparé rendrait impossible.
  */
  private readonly gridLayer = new Container();
  private readonly gridOverlay = new Graphics();
  private readonly footprintMark = new Graphics();
  /** Grille et vue pour lesquelles le quadrillage a été tracé, en clé comparable. */
  private gridOverlayKey = '';
  /** Emprise et validité pour lesquelles le losange a été tracé. */
  private footprintMarkKey = '';

  /**
   * Bâtiments et effets dans le MÊME conteneur trié que les tuiles.
   *
   * Les séparer en calques superposés serait plus simple, et faux : le bloc de terre
   * d'une tuile déborde d'une rangée entière, si bien qu'une tuile de devant doit
   * pouvoir masquer un bâtiment de derrière. Tout se trie ensemble par profondeur.
   */
  private readonly effectsLayer = new Container();

  /**
   * Pièces en vol (E§1.2). **Sur la scène, pas dans `world`** : elles rejoignent un
   * compteur HTML fixe. Dans le monde, un déplacement de caméra pendant leur vol les
   * ferait dériver et le point d'arrivée bougerait sous elles.
   */
  private readonly coinLayer = new Container();

  private coinFlight: CoinFlight | null = null;

  /** Nombres qui giclent (B.1) — lu par la sonde `?debug` via son accesseur. */
  floatingNumbers: FloatingNumbers | null = null;

  /** Célébrations à paliers (B.3) — gerbe de jalon, confettis de session. */
  celebrations: Celebrations | null = null;

  /** Ciel, étoiles et ombre du plateau (§7). Derrière tout le reste. */
  private sky: Sky | null = null;

  /*
    ⚠️ `windowGlow` a été RETIRÉ — il n'a jamais existé.

    Le champ était déclaré, lu par `applyNightLighting`, et **jamais instancié**. La
    documentation de cette classe décrivait pourtant la lueur des fenêtres comme ce qui
    « rend la nuit lisible plutôt que simplement sombre », et affirmait sans elle que les
    tours vitrées deviennent « des rectangles noirs indiscernables ». Le défaut décrit
    était donc réel et présent, et le code censé le corriger n'a jamais tourné.

    Du code qui se lit comme une feature livrée alors qu'il ne fait rien est pire qu'une
    absence : il empêche de voir ce qui manque. L'effet reste à faire, et il est consigné
    comme tel dans l'audit (§ V2) plutôt que simulé ici.
  */
  private lastNight = -1;

  /** Dernière passe d'éclairage vivant (fenêtres, lampadaires) — cadencée à 4 Hz. */
  private lastLightingPass = 0;

  /** Circulation sur le réseau (E§1.8). Les routes vides font un décor, pas une ville. */
  private traffic: Traffic | null = null;

  private readonly tileSprites = new Map<string, Sprite>();
  private readonly skirtSprites = new Map<string, Sprite>();
  private skirtTexture: Texture | null = null;

  /** Sprites de route, une par case du réseau. */
  private readonly roadSprites = new Map<string, Sprite>();

  /**
   * Tweens de déroulement en cours, par case.
   *
   * Sans cette table, l'élagage d'une route détruisait son sprite en laissant tourner
   * l'animation qui l'anime : le tween doit être annulable depuis la branche de
   * destruction, pas seulement gardé (audit P0.1).
   */
  private readonly roadTweens = new Map<string, { cancel(): void }>();
  private readonly roadTextures = new Map<string, Texture>();

  /** Décor ambiant : arbres, rochers, bancs sur les cases restées libres. */
  private readonly propSprites = new Map<string, Sprite>();

  /** Modèle actuellement affiché par case — pour détecter un changement de contexte. */
  private readonly propNames = new Map<string, string>();
  private readonly propTextures = new Map<string, Texture>();

  /** Ombres au sol, une par bâtiment. Sans elles, la ville flotte. */
  private readonly shadowSprites = new Map<string, Sprite>();

  /**
   * Lueur des fenêtres, une par bâtiment (§7, E§).
   *
   * Un sprite par bâtiment plutôt qu'une passe de post-traitement : on ne dispose
   * d'aucune donnée sur l'emplacement réel des fenêtres des 281 modèles du pack, et en
   * fabriquer une reviendrait à ré-analyser toute la bibliothèque pour un gain que la
   * distance de vue annule. Ce qu'un joueur perçoit d'un immeuble éclairé à cette
   * échelle, c'est un HALO chaud, pas des carrés lumineux.
   *
   * Invisibles le jour — `visible = false`, donc zéro appel de dessin : l'effet ne coûte
   * rien pendant les deux tiers du cycle.
   */
  private readonly glowSprites = new Map<
    string,
    { readonly sprite: Sprite; readonly col: number; readonly row: number }
  >();
  private glowTexture: Texture | null = null;
  private shadowTexture: Texture | null = null;
  private readonly buildingSprites = new Map<string, Sprite>();

  /**
   * Le sprite d'un bâtiment, ou `null`.
   *
   * Exposé pour les sondes, et pour une raison précise : sans lui, un parcours ne peut
   * que regarder des pixels, donc il ne sait pas distinguer « le sprite est hors champ »
   * de « le sprite n'a jamais été créé ». C'est exactement la distinction entre un défaut
   * d'affichage et un achat en gemmes perdu (`probe:landmark`).
   */
  spriteFor(id: string): Sprite | null {
    return this.buildingSprites.get(id) ?? null;
  }
  private readonly buildingTextures = new Map<string, Texture>();

  private ghost: Sprite | null = null;
  private ghostType: string | null = null;

  /** Palier visuel que prendra le bâtiment posé — fourni par l'état de jeu. */
  private ghostLevel = 1;

  /** Jeton de génération : invalide un chargement de fantôme devenu obsolète. */
  private ghostGeneration = 0;
  private hoveredCell: GridCell | null = null;
  private readonly tweens = new TweenRunner({
    // Lu à chaque animation, pas figé au démarrage : le joueur peut changer le
    // réglage système en cours de partie.
    prefersReducedMotion: () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  });

  private grid: IsoGrid;
  private camera: Camera | null = null;
  private grassTexture: Texture | null = null;
  private lastFrameTime = 0;

  /**
   * Taille LOGIQUE du viewport, en pixels CSS.
   *
   * On la suit nous-mêmes plutôt que de lire `app.renderer.width` : avec
   * `resolution = devicePixelRatio`, ce champ renvoie la taille du tampon en pixels
   * physiques. Sur un téléphone en DPR 3, centrer la caméra dessus décalerait la ville
   * hors de l'écran — un bug qui ne se voit pas sur un moniteur de bureau en DPR 1.
   */
  private viewport: { width: number; height: number } = { width: 1, height: 1 };

  private resizeObserver: ResizeObserver | null = null;

  /**
   * Mesure la fréquence d'images et abaisse le niveau de détail si la machine ne suit
   * pas. Alimenté par la boucle de rendu, jamais par une horloge à lui : c'est ce qui
   * évite qu'un onglet masqué ne fasse conclure à une machine lente.
   */
  private readonly quality = new QualityManager();

  constructor(private readonly options: CityRendererOptions) {
    this.grid = options.grid;
  }

  async init(): Promise<void> {
    this.viewport = this.measureViewport();
    this.sky?.resize(this.viewport.width, this.viewport.height);

    await this.app.init({
      canvas: this.options.canvas,
      width: this.viewport.width,
      height: this.viewport.height,
      background: BACKGROUND_COLOR,
      // Le ciel couvre l'écran ; cette couleur ne se voit que la toute première frame.
      antialias: false,
      // Les tuiles source font 512 px pour un rendu bien plus petit : sans mipmaps,
      // la réduction scintille dès que la caméra bouge.
      resolution: Math.min(globalThis.devicePixelRatio, 2),
      autoDensity: true,
      powerPreference: 'high-performance',
    });

    TextureSource.defaultOptions.autoGenerateMipmaps = true;
    this.grassTexture = await cityTexture(GRASS_TILE_NAME);
    this.skirtTexture = await cityTexture(ISLAND_SKIRT_NAME);

    /*
      Quatre orientations par véhicule, indexées par le NUMÉRO DE ROTATION du pipeline.

      Le nom d'asset accompagne les textures : c'est lui qui donne accès à l'orientation
      de base mesurée (`facing.generated.ts`). Sans lui, le trafic ne pouvait qu'utiliser
      l'indice de direction comme indice de rotation — la faute qui faisait rouler la
      moitié des voitures en marche arrière.
    */
    const cars = await Promise.all(
      ['car-1', 'car-2', 'car-3', 'car-4'].map(async (asset) => ({
        asset,
        frames: await Promise.all(
          ['', '-r1', '-r2', '-r3'].map(async (turn) => cityTexture(`${asset}${turn}`)),
        ),
      })),
    );
    // Le trafic écrit directement dans le calque TRIÉ : c'est la seule façon pour un
    // véhicule d'avoir sa propre profondeur (voir le constructeur de `Traffic`).
    this.traffic = new Traffic(cars, this.tileLayer);
    this.shadowTexture = CityRenderer.makeShadowTexture(this.app.renderer);
    this.glowTexture = CityRenderer.makeGlowTexture();

    // `sortableChildren` laisse PixiJS ordonner par `zIndex` : tuiles, bâtiments et
    // effets d'une même case restent alors dans le bon ordre sans calques séparés.
    this.tileLayer.sortableChildren = true;
    this.world.addChild(this.tileLayer, this.gridLayer, this.effectsLayer);
    this.effectsLayer.zIndex = 1;
    this.gridLayer.addChild(this.gridOverlay);
    this.gridLayer.visible = false;
    // L'empreinte est invisible tant qu'aucune case n'est visée : `moveGhostTo` la
    // rallume. Elle vit dans le calque TRIÉ, donc elle porte sa propre profondeur.
    this.footprintMark.visible = false;
    this.tileLayer.addChild(this.footprintMark);
    // Le ciel passe SOUS le monde : ajouté en premier, il ne trie pas avec lui.
    this.sky = new Sky();
    this.sky.resize(this.viewport.width, this.viewport.height);
    this.app.stage.addChild(this.sky.container, this.world, this.coinLayer);

    // B.3 — les célébrations partagent le calque écran des pièces : jalons et
    // confettis se jouent au-dessus du monde, jamais dedans.
    this.celebrations = new Celebrations(this.app.renderer, this.coinLayer, this.tweens);

    this.buildTiles();

    this.camera = new Camera(
      this.options.canvas,
      gridWorldBounds(this.grid, SYNTY_TILE),
      this.viewport,
      absoluteZoomLimits(),
    );
    this.camera.focusCenter(initialZoom());
    this.camera.attach();

    this.app.ticker.add(this.onFrame);

    // `ResizeObserver` plutôt que l'événement `resize` de la fenêtre : il capte aussi
    // l'ouverture des outils de développement, la rotation, l'apparition ou la disparition
    // de la barre d'URL mobile, et il se déclenche une première fois dès l'observation —
    // ce qui rattrape une mise en page pas encore stabilisée au démarrage.
    this.resizeObserver = new ResizeObserver(this.onResize);
    this.resizeObserver.observe(this.options.container);
  }

  /** Change la taille de la grille et rebâtit le calque de tuiles (§6, achat de terrain). */
  setGridSize(deltaCols: number, deltaRows: number): void {
    this.grid = resizeGrid(this.grid, deltaCols, deltaRows);
    this.buildTiles();
    this.camera?.setBounds(gridWorldBounds(this.grid, SYNTY_TILE));
  }

  /**
   * Installe le vol des pièces. Séparé de `init()` parce que la cible est le compteur
   * HTML, que le renderer ne connaît pas — c'est la couche de câblage qui la fournit.
   */
  enableCoinFlight(
    target: () => ScreenPoint,
    onArrival: (value: number) => void,
    formatGain?: (value: number) => string,
  ): void {
    // B.1 — le nombre gicle au DÉPART de chaque pièce, avec la valeur qu'elle porte.
    // Sans `formatGain` (page visiteur, tests de rendu), les pièces volent sans nombre.
    if (formatGain !== undefined) {
      this.floatingNumbers = new FloatingNumbers({
        layer: this.coinLayer,
        tweens: this.tweens,
        format: formatGain,
      });
    }

    this.coinFlight = new CoinFlight(this.app.renderer, {
      layer: this.coinLayer,
      tweens: this.tweens,
      target,
      onArrival,
      onLaunch: (from, value, now) => {
        this.floatingNumbers?.emit(from, value, now);
      },
    });
  }

  /**
   * Lance une pièce depuis une case de la grille. Rend `false` si le plafond de juice
   * a absorbé le gain : l'appelant doit alors créditer sans animation (E§3).
   */
  emitCoin(cell: GridCell, value: number, now: number, type = ''): boolean {
    if (this.coinFlight === null) return false;
    return this.coinFlight.emit(this.worldToScreen(this.coinOrigin(type, cell)), value, now);
  }

  /**
   * Un bâtiment SURSAUTE quand il paie (ROADMAP 4.4, E§1).
   *
   * ══ Pourquoi ici, et pas sur une horloge ════════════════════════════════════
   *
   * La tentation était de faire respirer tous les bâtiments en boucle. Quarante sprites
   * qui pulsent en permanence, ce n'est pas de la vie : c'est du bruit, et l'œil cesse de
   * le voir en trente secondes — tout en payant le coût d'animation à chaque image.
   *
   * Le sursaut est accroché à la pièce qui décolle. **Une cause visible, un effet
   * visible, au même instant** : le joueur apprend sans qu'on lui explique quel bâtiment
   * vient de rapporter. Et comme les pièces tournent d'un bâtiment à l'autre, la ville
   * entière frémit à tour de rôle sans que rien ne bouge en continu.
   *
   * ── Les trois précautions ──────────────────────────────────────────────────
   *
   * · **on n'anime QUE l'échelle verticale**, pas la position. Un sprite de bâtiment est
   *   ancré à sa base (contrat d'ancrage, règle 20) : le déplacer verticalement le
   *   décollerait du sol, ce qui se lit comme un bug de rendu, pas comme de la vie ;
   * · **un sursaut déjà en cours n'est pas relancé.** Deux pièces rapprochées sur le même
   *   bâtiment produiraient sinon un tremblement au lieu d'un rebond ;
   * · **le mouvement réduit est respecté sans code ici** : `TweenRunner` saute à la valeur
   *   finale et appelle quand même `onComplete` (étape 4a). L'échelle revient donc à 1, ce
   *   qui est exactement le bon comportement — pas d'animation, pas de sprite figé de
   *   travers.
   */
  bumpBuilding(id: string, now: number): void {
    const sprite = this.buildingSprites.get(id);
    if (sprite === undefined || sprite.destroyed) return;
    if (this.bumping.has(id)) return;

    this.bumping.add(id);
    const base = sprite.scale.y;

    this.tweens.add(
      {
        from: 0,
        to: 1,
        durationMs: 260,
        onUpdate: (value) => {
          if (sprite.destroyed) return;
          /*
          Une bosse, pas une rampe : l'échelle monte puis redescend en un seul tween.
          `sin(πt)` vaut 0 aux deux bouts et 1 au milieu — donc le sprite finit
          exactement à son échelle d'origine, même si le tween est coupé net par le
          mouvement réduit.
        */
          sprite.scale.y = base * (1 + Math.sin(Math.PI * value) * BUMP_AMOUNT);
        },
        onComplete: () => {
          if (!sprite.destroyed) sprite.scale.y = base;
          this.bumping.delete(id);
        },
      },
      now,
    );
  }

  /** Bâtiments en train de sursauter — un seul rebond à la fois par bâtiment. */
  private readonly bumping = new Set<string>();

  /**
   * Point de départ d'une pièce : le centre de l'emprise, dans le repère de la VUE.
   *
   * Deux erreurs cumulées faisaient décoller les pièces du vide. `cellToWorld` était
   * appelé sans `toView`, donc après un pivot les pièces partaient de l'ancienne
   * position du bâtiment ; et la case transmise était celle d'ANCRAGE, donc pour un
   * 2 × 2 la pièce partait du coin supérieur même sans pivot. `footprintCenter` règle
   * les deux d'un coup — la fonction existait déjà, elle n'était pas appelée ici.
   */
  private coinOrigin(type: string, cell: GridCell): { x: number; y: number } {
    return this.footprintCenter(type, cell);
  }

  /** Origine d'une pièce en coordonnées écran — réservé à la sonde pivot (E§3). */
  coinOriginForProbe(building: { type: string; col: number; row: number }): ScreenPoint {
    return this.worldToScreen(this.coinOrigin(building.type, building));
  }

  /**
   * Monde → écran, en pixels CSS. Exact inverse de la transformation appliquée au
   * conteneur `world` à chaque frame : si l'une change, l'autre doit suivre.
   */
  worldToScreen(point: { x: number; y: number }): ScreenPoint {
    const camera = this.camera;
    if (camera === null) return { x: 0, y: 0 };
    const { x, y, zoom } = camera.getState();
    return {
      x: (point.x - x) * zoom + this.viewport.width / 2,
      y: (point.y - y) * zoom + this.viewport.height / 2,
    };
  }

  getStatus(): RendererStatus {
    return {
      grid: this.grid,
      zoom: this.camera?.getState().zoom ?? 0,
      fps: this.app.ticker.FPS,
      tiles: this.tileLayer.children.length,
      quality: this.quality.getProfile().level,
    };
  }

  /**
   * Zoom relatif autour du centre de l'écran.
   *
   * Facteur multiplicatif et non additif : un pas constant en valeur absolue paraît
   * énorme quand on est dézoomé et imperceptible quand on est proche. Les butées de
   * `balance.ts` s'appliquent, donc un appui répété s'arrête proprement.
   */
  zoomBy(factor: number): void {
    this.camera?.zoomBy(factor);
  }

  /**
   * Aligne la grille du rendu sur celle de l'état de jeu.
   *
   * L'état fait foi (§4) : c'est le snapshot qui porte la taille du terrain, sauvegarde
   * comprise. Le renderer ne décide pas de grandir, il suit.
   */
  setGridTo(grid: IsoGrid): void {
    if (grid.cols === this.grid.cols && grid.rows === this.grid.rows) return;
    this.grid = createGrid(grid.cols, grid.rows);
    this.buildTiles();
    this.camera?.setBounds(gridWorldBounds(this.grid, SYNTY_TILE));
  }

  /**
   * Déclare les bandes d'écran masquées par l'interface (ROADMAP 1.2).
   *
   * Le renderer ne les MESURE pas lui-même : `src/render` n'a pas à connaître les
   * classes du HUD. C'est `game.ts` qui lit le DOM et les lui passe, comme il lui passe
   * déjà le viewport.
   */
  setViewportInsets(insets: ViewportInsets): void {
    this.camera?.setInsets(insets);
  }

  /** Rétablit le cadrage de départ : grille centrée, tuiles à leur taille de référence. */
  recenter(): void {
    this.camera?.focusCenter(initialZoom());
  }

  destroy(): void {
    this.traffic?.destroy();
    this.sky?.destroy();
    this.coinFlight?.destroy();
    this.resizeObserver?.disconnect();
    this.app.ticker.remove(this.onFrame);
    this.camera?.detach();
    this.app.destroy(false, { children: true });
  }

  private buildTiles(): void {
    const texture = this.grassTexture;
    if (texture === null) {
      return;
    }

    // ⚠️ Ne détruire QUE les tuiles dont cette méthode est propriétaire.
    //
    // Le calque est délibérément partagé avec les bâtiments et le fantôme : c'est ce
    // partage qui rend l'occlusion isométrique correcte, une tuile de premier plan
    // devant pouvoir masquer un bâtiment situé derrière (`zIndex = depthOf * 2`, les
    // bâtiments prenant `+ 1`). Deux conteneurs séparés casseraient cet entrelacement.
    //
    // Mais partager un conteneur, c'est partager la portée d'un `removeChildren()`.
    // La version précédente vidait et détruisait TOUT le calque : agrandir la grille
    // détruisait chaque bâtiment du joueur, pendant que `buildingSprites` conservait
    // des références mortes — donc `syncBuildings` les considérait comme déjà présents
    // et ne les recréait jamais. La ville disparaissait définitivement, alors que
    // l'état de jeu, lui, était intact.
    for (const sprite of this.tileSprites.values()) {
      this.tileLayer.removeChild(sprite);
      sprite.destroy();
    }
    this.tileSprites.clear();

    for (const sprite of this.skirtSprites.values()) {
      this.tileLayer.removeChild(sprite);
      sprite.destroy();
    }
    this.skirtSprites.clear();

    // L'ordre d'ajout EST l'ordre de dessin : `forEachCellInDrawOrder` garantit qu'une
    // tuile est posée après celles qu'elle peut recouvrir (le bloc de terre déborde
    // d'une rangée entière).
    forEachCellInDrawOrder(this.grid, (cell) => {
      const sprite = new Sprite(texture);
      sprite.anchor.set(SYNTY_TILE.anchorX, SYNTY_TILE.anchorY);

      /*
        Teinte variable par case, déterministe.

        Un aplat vert rigoureusement uniforme sur cent cases se lit comme du papier
        peint : c'est le motif régulier, pas la couleur, que l'œil rejette. Une
        variation de quelques pour cent suffit à casser l'uniformité sans qu'aucune
        case ne se distingue.
      */
      const noise = (Math.sin(cell.col * 12.9898 + cell.row * 78.233) * 43758.5453) % 1;
      const shade = 1 - Math.abs(noise) * 0.09;
      sprite.tint = ((255 * shade) << 16) | ((255 * shade) << 8) | (255 * shade * 0.99);

      // Tout ce qui est posé sur la grille passe par la vue : sol, routes, décor,
      // bâtiments. N'en faire suivre qu'une partie désolidarise la ville du terrain.
      const view = cell;
      const position = cellToWorld(view, SYNTY_TILE);
      sprite.position.set(position.x, position.y);
      // Deux plans par rangée : la tuile, puis ce qui se pose dessus.
      sprite.zIndex = depthOf(view) * 2;

      this.tileSprites.set(cellKey(cell), sprite);
      this.tileLayer.addChild(sprite);

      /*
        Sous-face uniquement sur les DEUX ARÊTES AVANT.

        En projection isométrique, seules les faces `col = max` et `row = max` regardent
        le spectateur ; les deux autres arêtes sont cachées derrière le plateau. Les
        garnir aussi ne coûtait pas que des sprites inutiles : agrandies pour fusionner,
        leurs pointes dépassaient AU-DESSUS du plateau et faisaient une frange noire sur
        les bords arrière.
      */
      // Bordure calculée sur la case AFFICHÉE : les arêtes visibles changent avec la vue.
      const onBorder = view.col === this.grid.cols - 1 || view.row === this.grid.rows - 1;

      if (onBorder && this.skirtTexture !== null) {
        const skirt = new Sprite(this.skirtTexture);
        skirt.anchor.set(0.5, 0.5);
        // Agrandies pour se CHEVAUCHER : à l'échelle 1 les sous-faces formaient une
        // rangée de dents séparées au lieu d'une masse rocheuse continue. Le
        // recouvrement fait fusionner leurs silhouettes.
        skirt.scale.set(1.7);
        skirt.position.set(position.x, position.y + SYNTY_TILE.blockHeight * 0.4);
        // Juste SOUS sa tuile dans le tri : la sous-face pend, elle ne recouvre rien.
        skirt.zIndex = depthOf(view) * 2 - 1;
        this.skirtSprites.set(cellKey(cell), skirt);
        this.tileLayer.addChild(skirt);
      }
    });
  }

  /* ── Bâtiments ─────────────────────────────────────────────────────── */

  /**
   * Nom du sprite d'un bâtiment : type, palier visuel, quart de tour.
   *
   * L'orientation 0 garde le nom nu — c'est elle que la boutique affiche, et un
   * suffixe systématique obligerait chaque appelant à connaître la convention.
   */
  private static spriteName(
    type: string,
    level: number,
    orientation: number,
    variant: number,
  ): string {
    /*
      ⚠️ Un point de repère n'existe QU'EN PALIER 1 : le pack n'en rend qu'un modèle.

      Demander ${tick}waterTower-t2.png${tick} rendait une texture nulle, donc AUCUN sprite : le
      bâtiment disparaissait de la ville alors qu'il était bien dans l'état de jeu. La
      commande refuse désormais de l'améliorer, mais la ceinture reste ici — une
      sauvegarde d'avant le correctif porte encore un niveau 2, et elle doit s'afficher.
    */
    const tier = isLandmark(type) ? 1 : Math.min(3, Math.max(1, level));
    const turn = ((orientation % 4) + 4) % 4;
    const available = Math.max(1, buildingByType(type)?.variants ?? 1);
    const model = ((variant % available) + available) % available;
    return `${type}-t${String(tier)}${model === 0 ? '' : `-v${String(model)}`}${turn === 0 ? '' : `-r${String(turn)}`}.png`;
  }

  /** Charge la texture d'un sprite, une seule fois. */
  private async textureFor(
    type: string,
    level = 1,
    orientation = 0,
    variant = 0,
  ): Promise<Texture | null> {
    if (buildingByType(type) === undefined) return null;

    const name = CityRenderer.spriteName(type, level, orientation, variant);
    const cached = this.buildingTextures.get(name);
    if (cached !== undefined) return cached;

    const texture = await cityTexture(name);
    this.buildingTextures.set(name, texture);
    return texture;
  }

  /**
   * Position monde d'un bâtiment, **centrée sur son emprise**.
   *
   * `cellToWorld(col, row)` rend le centre de la case d'ANCRAGE. Pour un bâtiment de
   * 1 × 1 c'est aussi le centre du bâtiment ; pour un 2 × 2 c'est le centre du quart
   * supérieur, et le sprite se retrouve décalé d'une demi-case en haut à gauche de
   * l'emprise qu'il occupe réellement. Le décalage est exactement ce que le joueur voit
   * comme « ça ne se pose pas au bon endroit ».
   *
   * La projection étant linéaire en (col, row), le centre du bloc s'obtient avec des
   * coordonnées fractionnaires — aucune trigonométrie, aucun cas particulier.
   */
  private footprintCenter(type: string, cell: GridCell): { x: number; y: number } {
    const offset = (footprintOf(type) - 1) / 2;
    const centre = { col: cell.col + offset, row: cell.row + offset };
    return cellToWorld(centre, SYNTY_TILE);
  }

  /**
   * Profondeur de tri d'un bâtiment : celle de sa case la PLUS proche du spectateur.
   *
   * Un 2 × 2 trié sur son coin arrière passe derrière une tuile qu'il recouvre : il faut
   * la case la plus proche du spectateur, pas la case d'ancrage.
   *
   * ⚠️ Il y avait ici une SECONDE erreur, disparue avec la rotation de vue (0.55.0) : le
   * coin « le plus proche » n'était pas toujours `(col+size-1, row+size-1)` — à view = 2
   * c'était le coin opposé, et le calcul se faisait en repère ÉTAT alors que tuiles,
   * routes, props et trafic se triaient en repère VUE. Un bâtiment passait alors sous sa
   * propre tuile. Le repère est maintenant unique : la question ne peut plus se poser.
   *
   * On projette les quatre coins de l'emprise et on garde la profondeur maximale : le
   * bon coin est choisi par construction, quelle que soit l'orientation — plutôt qu'une
   * table de cas par vue, qui se serait trompée (règle 15).
   */
  private footprintDepth(type: string, cell: GridCell): number {
    const size = footprintOf(type);
    const last = size - 1;
    let deepest = -Infinity;

    for (const dc of [0, last]) {
      for (const dr of [0, last]) {
        deepest = Math.max(deepest, depthOf({ col: cell.col + dc, row: cell.row + dr }));
      }
    }

    return deepest;
  }

  /**
   * Sprite d'un bâtiment : ancre dérivée du contrat de cadrage, échelle garde-fou.
   *
   * ── L'ancre ─────────────────────────────────────────────────────────────────
   *
   * Elle ne vaut plus 0,5 en vertical. Un bâtiment est cadré au plus juste sous sa base
   * (sinon `doctor-t1` réclamerait une texture de 4574 px, au-delà de la limite des GPU
   * mobiles) et l'ancre se CALCULE depuis l'emprise et la hauteur de la texture —
   * jamais depuis une table. Voir `buildingAnchorY`.
   *
   * ── L'échelle : AUCUNE correction ici, et c'est un choix ────────────────────
   *
   * Un sprite de bâtiment se dessine à l'échelle 1. Corriger sa largeur côté moteur a
   * été essayé deux fois et jeté deux fois :
   *
   * - agrandir de ×3 étire des pixels qui n'existent pas et rend la ville molle ;
   * - **ramener chaque modèle à la largeur de son emprise détruit l'échelle humaine.**
   *   C'est la leçon coûteuse de la v0.8.0 : un modèle naturellement petit gonflé et un
   *   grand rétréci donnent des fenêtres, des portes et des étages de tailles
   *   différentes d'un immeuble au suivant. Mesuré, un facteur 1,7 entre `doctor-t1`
   *   (1,22 case au naturel, étiré à 2) et `house-t3` — et l'œil le lit aussitôt comme
   *   faux, même sans savoir pourquoi.
   *
   * Le pipeline rend donc tous les bâtiments à l'échelle du pack (100 unités = 1 case =
   * 5 m), bornée par l'emprise. `buildingScale()` n'est plus une correction à appliquer
   * mais un **contrôle** : elle doit rester ≥ 1, c'est-à-dire « aucun bâtiment plus large
   * que sa parcelle ». `tests/spriteFrame.test.ts` le vérifie.
   */
  private makeBuildingSprite(texture: Texture, definition: BuildingDefinition): Sprite {
    const sprite = new Sprite(texture);
    CityRenderer.applyBuildingFrame(sprite, definition.type);
    return sprite;
  }

  /**
   * Ancre d'un sprite de bâtiment, dérivée de son contrat de cadrage.
   *
   * Appelée à la création **et à chaque échange de texture** : le fantôme change de
   * variante à chaque case survolée, et deux variantes sont deux modèles différents,
   * donc deux hauteurs d'image différentes. Garder l'ancre de la précédente ferait
   * flotter ou enfoncer l'aperçu — et un aperçu qui mente est pire que pas d'aperçu.
   */
  private static applyBuildingFrame(sprite: Sprite, type: string): void {
    sprite.anchor.set(0.5, buildingAnchorY(footprintOf(type), sprite.texture.height));
  }

  /*
    ══ TROIS JETONS DE GÉNÉRATION, un par passe de synchronisation ═════════════

    Les trois `sync*` sont `async` et tous leurs appelants les lancent en `void` : une
    pose, un achat de terrain et un pivot de vue peuvent donc se chevaucher, et chacun
    rend la main au milieu de sa boucle sur un `await cityTexture`. Deux passes
    entrelacées sur la même carte de sprites créent le même objet deux fois, ou
    détruisent celui que l'autre vient de poser.

    Le défaut est intermittent par nature — il dépend du moment où une texture arrive du
    réseau, donc du cache et de la latence. Il ne se reproduit pas ; il s'évite.

    Le jeton est incrémenté à l'entrée et relu après chaque attente : une passe devenue
    obsolète ABANDONNE. Elle peut laisser un travail à moitié fait, et c'est sans
    conséquence — la passe qui l'a invalidée resynchronise tout depuis l'état, qui fait
    foi. Sérialiser les passes dans une file aurait fait l'inverse : exécuter jusqu'au
    bout un travail qu'on sait périmé.
  */
  private buildingsGeneration = 0;
  private roadsGeneration = 0;
  private propsGeneration = 0;

  /**
   * Aligne les sprites sur l'état du jeu. Les bâtiments déjà posés ne sont pas
   * recréés : seuls les nouveaux apparaissent, et c'est eux qui reçoivent le juice.
   */
  async syncBuildings(
    buildings: readonly PlacedBuilding[],
    animateNew = true,
    rebuild = false,
  ): Promise<void> {
    const generation = ++this.buildingsGeneration;
    // `rebuild` : la vue a pivoté, donc TOUTES les positions et orientations changent.
    // Repositionner sans recréer laisserait chaque bâtiment tourné vers l'ancien angle.
    if (rebuild) {
      for (const id of [...this.buildingSprites.keys()]) this.forget(id);
    }

    const seen = new Set<string>();

    for (const building of buildings) {
      seen.add(building.id);

      // Un sprite détruit ne compte pas comme présent. Sans ce garde-fou, la seule
      // trace d'un bâtiment perdu était une entrée morte dans la `Map`, et la ville
      // ne se réparait jamais — même en resynchronisant. L'état de jeu fait foi.
      const existing = this.buildingSprites.get(building.id);
      if (existing !== undefined) {
        if (!existing.destroyed) continue;
        this.buildingSprites.delete(building.id);
      }

      const texture = await this.textureFor(
        building.type,
        building.level,
        building.orientation,
        building.variant,
      );
      // Une passe plus récente a pris la main pendant le chargement : elle a l'état
      // frais, celle-ci ne ferait que poser des sprites qu'il faudrait défaire.
      if (generation !== this.buildingsGeneration) return;

      const definition = buildingByType(building.type);
      if (texture === null || definition === undefined) continue;

      const sprite = this.makeBuildingSprite(texture, definition);
      const cell: GridCell = { col: building.col, row: building.row };
      const position = this.footprintCenter(building.type, cell);

      sprite.position.set(position.x, position.y);
      sprite.zIndex = this.footprintDepth(building.type, cell) * 2 + 1;

      this.buildingSprites.set(building.id, sprite);
      this.tileLayer.addChild(sprite);

      if (this.shadowTexture !== null) {
        const shadow = new Sprite(this.shadowTexture);
        shadow.anchor.set(0.5, 0.5);
        shadow.position.set(position.x, position.y);
        // Aplatie au rapport du losange : une ombre ronde sur un sol isométrique se
        // lit comme une sphère posée à côté du bâtiment.
        shadow.width = SYNTY_TILE.width * footprintOf(building.type) * 0.78;
        shadow.height = SYNTY_TILE.height * footprintOf(building.type) * 0.78;
        // Entre la tuile et le bâtiment : l'ombre se pose sur le sol et la route.
        shadow.zIndex = this.footprintDepth(building.type, cell) * 2 + 0.75;
        this.shadowSprites.set(building.id, shadow);
        this.tileLayer.addChild(shadow);
      }

      if (this.glowTexture !== null) {
        const glow = new Sprite(this.glowTexture);
        glow.anchor.set(0.5, 0.5);
        /*
          La lueur se pose sur le CORPS du bâtiment, pas à sa base.

          Les fenêtres sont dans la partie haute ; un halo centré sur la case éclairerait
          le trottoir. On remonte donc d'un tiers de la hauteur RENDUE du sprite — celle
          du modèle réel, pas une constante, les hauteurs variant du simple au sextuple
          entre une échoppe et un cabinet médical (règle 14).
        */
        glow.position.set(position.x, position.y - sprite.height * 0.38);
        /*
          ⚠️ La taille vient de la LARGEUR DU BÂTIMENT, pas de celle de la case.

          Livré d'abord à `TILE_WIDTH × emprise × 0,95`, soit 486 px de halo pour une
          maison d'une case : le bâtiment disparaissait dans sa propre lumière, et
          quatre voisins se noyaient dans la même flaque. Une tour étroite recevait le
          même disque qu'un entrepôt large.

          Un immeuble éclairé est visible à peu près à sa propre largeur : c'est cette
          mesure-là qui a du sens, et elle est déjà là — `sprite.width` porte le modèle
          réel, mis à l'échelle du pack (règle 19).
        */
        const span = sprite.width * 0.82;
        glow.width = span;
        // Aplati : vu de trois quarts, une lumière de façade s'étale en largeur.
        glow.height = span * 0.52;
        // Additif : une lumière AJOUTE au fond, elle ne le remplace pas. En mélange
        // normal, le halo lave la façade en gris et le bâtiment perd sa silhouette.
        glow.blendMode = 'add';
        glow.visible = false;
        // Au-dessus du bâtiment de sa propre case (+1), sous ceux de devant. Une lumière
        // qui traverserait la tour d'à côté trahirait qu'elle est plaquée.
        glow.zIndex = this.footprintDepth(building.type, cell) * 2 + 1.5;
        /*
          ⚠️ La CASE voyage avec la lueur, elle ne se relit pas ailleurs.

          Première version : l'intensité se lisait dans `placedIndex`. Or cette table
          n'est remplie qu'à la POSE, par `game.ts` — jamais au chargement d'une
          sauvegarde. Toutes les fenêtres d'une ville rechargée seraient donc restées
          éteintes, et le défaut ne se serait vu qu'à la tombée de la nuit, plusieurs
          minutes après le rechargement.
        */
        this.glowSprites.set(building.id, { sprite: glow, col: cell.col, row: cell.row });
        this.tileLayer.addChild(glow);
      }

      // ROADMAP 3.4 — le palier se lit sur la ville, pas seulement dans un panneau.
      this.paintLevelPip(
        building.id,
        building.level,
        sprite,
        this.footprintDepth(building.type, cell) * 2,
      );

      if (animateNew) {
        this.playPlacement(building, sprite, position);
      }
    }

    for (const [id, glow] of this.glowSprites) {
      if (!seen.has(id)) {
        this.tileLayer.removeChild(glow.sprite);
        glow.sprite.destroy();
        this.glowSprites.delete(id);
      }
    }

    for (const [id, shadow] of this.shadowSprites) {
      if (!seen.has(id)) {
        this.tileLayer.removeChild(shadow);
        shadow.destroy();
        this.shadowSprites.delete(id);
      }
    }

    for (const [id, entry] of this.levelPips) {
      if (seen.has(id)) continue;
      this.tileLayer.removeChild(entry.pip);
      entry.pip.destroy();
      this.levelPips.delete(id);
    }

    for (const [id, sprite] of this.buildingSprites) {
      if (seen.has(id)) continue;
      sprite.destroy();
      this.buildingSprites.delete(id);
    }
  }

  /* ── Pastilles de palier (ROADMAP 3.4) ──────────────────────────────────── */

  /**
   * Une pastille par bâtiment AMÉLIORÉ, jamais par bâtiment.
   *
   * ══ Deux décisions, et elles se défendent ════════════════════════════════
   *
   * 1. **Le palier 1 ne porte rien.** C'est l'état par défaut : le silence le dit. Une
   *    pastille sur chacun des soixante bâtiments d'une ville ferait exactement ce que
   *    la 3.4 interdit — un tableau de bord à la place d'une ville — et n'ajouterait
   *    aucune information, puisque « pas de pastille » signifie déjà « palier 1 ».
   * 2. **Des points, pas un chiffre.** Deux points valent « palier 2 » sans police, sans
   *    traduction, et restent lisibles à quinze pixels de haut là où un « 2 » devient
   *    une tache. C'est aussi l'idiome du genre.
   *
   * Elles vivent dans le calque TRIÉ, à la profondeur du bâtiment + 1,6 : au-dessus du
   * sien et de sa lueur, sous ceux qui sont devant lui. Un calque séparé les ferait
   * flotter par-dessus une tour située plus près, ce qui trahirait qu'elles sont
   * plaquées.
   */
  private readonly levelPips = new Map<
    string,
    { readonly pip: Graphics; readonly x: number; readonly topY: number }
  >();

  private paintLevelPip(id: string, level: number, sprite: Sprite, depth: number): void {
    if (level <= 1) return;

    const count = Math.min(3, Math.floor(level));
    const radius = 2.6;
    const gap = 8;
    const width = gap * (count - 1) + 16;
    const height = 13;

    const pip = new Graphics();
    pip.roundRect(-width / 2, -height / 2, width, height, height / 2).fill({
      color: 0x0f1724,
      alpha: 0.74,
    });
    for (let i = 0; i < count; i++) {
      pip.circle(-((count - 1) * gap) / 2 + i * gap, 0, radius).fill({ color: 0xf7b23b });
    }

    /*
      ⚠️ Le HAUT du sprite se dérive de son ancre, il ne se devine pas.

      Les hauteurs vont du simple au sextuple entre une échoppe et un cabinet médical, et
      l'ancre d'un bâtiment est calculée depuis sa propre texture (règle 20). Un décalage
      constant collerait la pastille au toit des uns et à mi-façade des autres.
    */
    const topY = sprite.y - sprite.anchor.y * sprite.height;
    pip.zIndex = depth + 1.6;
    // Opacité ET échelle sont pilotées par le zoom à chaque frame ; on part invisible
    // pour qu'une pastille ne clignote pas à pleine opacité le temps d'une frame.
    pip.alpha = 0;
    pip.visible = false;
    this.levelPips.set(id, { pip, x: sprite.x, topY });
    this.tileLayer.addChild(pip);
  }

  /**
   * Fond les pastilles avec le zoom — elles disparaissent quand on prend du recul.
   *
   * Le seuil vit dans `balance.ts` et s'exprime en LARGEUR DE TUILE à l'écran, comme les
   * autres bornes de caméra : c'est ce que le joueur perçoit, et ça se règle sans
   * conversion. La transition est progressive entre ce seuil et le cadrage de départ —
   * une apparition sèche se lirait comme un défaut d'affichage.
   */
  private updateLevelPips(): void {
    if (this.levelPips.size === 0) return;

    const zoom = this.camera?.getState().zoom ?? 0;
    const tileWidth = zoom * SYNTY_TILE.width;
    const from = CAMERA_BALANCE.pipTileScreenWidth;
    const to = CAMERA_BALANCE.initialTileScreenWidth;
    /*
      Le dénominateur passe par `Math.max(1, …)` plutôt que par un garde `to <= from` :
      les deux bornes sont des littéraux `as const`, donc le compilateur SAIT que la
      comparaison est fausse aujourd'hui et ESLint refuse la branche morte. Le garde
      resterait pourtant utile le jour où l'on rapproche les deux réglages — cette
      forme-là survit à ce jour-là sans écrire de code que rien n'exécute.
    */
    const alpha = Math.max(0, Math.min(1, (tileWidth - from) / Math.max(1, to - from)));

    /*
      ══ LA PASTILLE A UNE TAILLE D'ÉCRAN, PAS UNE TAILLE DE MONDE ═════════════

      Livrée d'abord dessinée dans le monde : seize pixels de large au zoom de départ
      (×0,195) font **trois pixels à l'écran** — invisible. Et la mesure ne l'a pas vu,
      parce qu'elle regardait `alpha` et `visible`, tous deux parfaitement corrects.
      C'est la règle 24 dans son cas le plus net : le contrôle mesurait autre chose que
      ce qu'il prétendait, et seule la CAPTURE l'a montré.

      On contre donc le zoom — l'échelle vaut son inverse, et l'écart au toit se compte
      lui aussi en pixels d'écran. La pastille garde alors la même taille du plus large
      dézoom au plus près, ce qu'on attend d'une étiquette et non d'un décor.
    */
    const scale = zoom > 0 ? 1 / zoom : 1;
    for (const { pip, x, topY } of this.levelPips.values()) {
      if (pip.destroyed) continue;
      pip.scale.set(scale);
      pip.position.set(x, topY - PIP_SCREEN_GAP * scale);
      pip.alpha = alpha;
      pip.visible = alpha > 0.02;
    }
  }

  /** Callback déclenché à l'instant exact du contact, pour caler le son (E§1.1). */
  onPlacementImpact: (() => void) | null = null;

  private playPlacement(
    building: PlacedBuilding,
    sprite: Sprite,
    position: { x: number; y: number },
  ): void {
    const neighbours: Sprite[] = [];
    for (const [dCol, dRow] of [
      [1, 0],
      [0, 1],
      [-1, 0],
      [0, -1],
    ] as const) {
      const found = [...this.buildingSprites.entries()].find(([id]) => {
        const other = this.placedIndex.get(id);
        return other?.col === building.col + dCol && other.row === building.row + dRow;
      });
      if (found !== undefined) neighbours.push(found[1]);
    }

    playPlacementJuice(
      {
        building: sprite,
        tile: this.tileSprites.get(cellKey({ col: building.col, row: building.row })),
        neighbours,
        effectsLayer: this.effectsLayer,
        worldX: position.x,
        worldY: position.y,
      },
      {
        runner: this.tweens,
        now: performance.now(),
        onImpact: () => this.onPlacementImpact?.(),
      },
    );
  }

  /** Index des positions, pour retrouver les voisins sans reparcourir l'état. */
  private readonly placedIndex = new Map<string, { col: number; row: number }>();

  rememberPlacement(building: PlacedBuilding): void {
    this.placedIndex.set(building.id, { col: building.col, row: building.row });
  }

  /* ── Mode placement ────────────────────────────────────────────────── */

  /** Case sous un point de l'écran, ou `null` si hors grille. */
  cellAtScreen(screenX: number, screenY: number): GridCell | null {
    const camera = this.camera;
    if (camera === null) return null;

    const world = screenToWorld({ x: screenX, y: screenY }, camera.getState(), this.viewport);
    // La case lue à l'écran est une case AFFICHÉE : on la ramène dans le repère de
    // l'état de jeu. Sans cette inversion, après un quart de tour, cliquer une maison
    // en sélectionnait une autre — le pivot rendait le jeu injouable.
    const cell = worldToCell(world, SYNTY_TILE);
    return isInsideGrid(this.grid, cell) ? cell : null;
  }

  /**
   * Point d'écran au centre d'une case — l'inverse exact de `cellAtScreen`.
   *
   * Écrit pour les sondes : viser un bâtiment à l'écran demandait sinon de refaire la
   * projection, la vue pivotée et le cadrage caméra à la main. Une sonde qui recalcule
   * ce que le moteur calcule ne peut plus le contredire — elle ne mesure alors que sa
   * propre arithmétique (règle 22).
   */
  screenAtCell(cell: GridCell): { x: number; y: number } | null {
    const camera = this.camera;
    if (camera === null) return null;
    const world = cellToWorld(cell, SYNTY_TILE);
    return worldToScreen(world, camera.getState(), this.viewport);
  }

  /** Active le fantôme de placement pour un type, ou le retire avec `null`. */
  /**
   * Prépare le fantôme de placement.
   *
   * `level` est FOURNI par l'appelant : le palier visuel dépend du nombre
   * d'exemplaires déjà possédés, une information de l'état de jeu que le rendu ne
   * connaît pas et ne doit pas deviner.
   *
   * **Le fantôme doit montrer exactement ce qui sera posé.** Il chargeait
   * `textureFor(type)` avec les valeurs par DÉFAUT — palier 1, orientation 0, variante
   * 0 — alors que le bâtiment posé tire son palier du nombre possédé et son
   * orientation comme sa variante de la POSITION. L'aperçu montrait donc un autre
   * modèle, une autre taille et un autre angle que ce qui apparaissait au relâchement :
   * après dix bureaux, on visait une tour fine et on obtenait un immeuble deux fois
   * plus large. Un aperçu qui ment est pire que pas d'aperçu.
   *
   * Les quatre orientations (× variantes) sont préchargées ici pour que
   * `moveGhostTo` puisse échanger la texture **de façon synchrone** à chaque case
   * survolée : un `await` par mouvement de souris ferait clignoter le fantôme et
   * ouvrirait la course décrite en P4.2.
   */
  async setGhost(type: string | null, level = 1): Promise<void> {
    this.ghost?.destroy();
    this.ghost = null;
    this.ghostType = type;
    this.ghostLevel = level;

    if (type === null) return;

    const definition = buildingByType(type);
    if (definition === undefined) return;

    // Jeton de génération : une désélection pendant le chargement ne doit pas
    // ressusciter un fantôme que plus personne ne pilote (audit P4.2).
    const generation = ++this.ghostGeneration;

    const variants = Math.max(1, definition.variants);
    await Promise.all(
      Array.from({ length: 4 }, (_, turn) =>
        Promise.all(
          Array.from({ length: variants }, async (_unused, variant) =>
            this.textureFor(type, level, turn, variant),
          ),
        ),
      ),
    );

    if (generation !== this.ghostGeneration || this.ghostType !== type) return;

    const texture = await this.textureFor(type, level);
    if (texture === null) return;

    const sprite = this.makeBuildingSprite(texture, definition);
    sprite.alpha = 0.6;
    sprite.zIndex = 100_000;
    // Créé INVISIBLE : sans première case survolée, il apparaissait en (0,0) — sur
    // mobile, un bâtiment fantôme surgissait dans un coin avant le premier toucher.
    sprite.visible = false;
    this.ghost = sprite;
    this.tileLayer.addChild(sprite);
  }

  getGhostType(): string | null {
    return this.ghostType;
  }

  /**
   * Affiche ou masque le quadrillage de pose.
   *
   * « Grille visible UNIQUEMENT pendant la pose » (LIVRAISON 0.2) : le reste du temps,
   * une ville se regarde, elle ne se lit pas sur un damier. C'est aussi ce qui rend le
   * repère utile — il apparaît au moment exact où l'on cherche un emplacement.
   *
   * Le tracé est mis en cache sur (grille, vue) : le reconstruire à chaque ouverture du
   * mode pose coûterait 1 156 losanges sur une grille maximale, pour un dessin
   * identique. Il se refait quand le terrain s'agrandit ou que la vue pivote — les deux
   * seuls changements qui le rendent faux.
   */
  /** Voir `Camera.setDragToPan` : pendant une pose, le doigt appartient au bâtiment. */
  setDragToPan(enabled: boolean): void {
    this.camera?.setDragToPan(enabled);
  }

  setPlacementGrid(visible: boolean): void {
    this.gridLayer.visible = visible;
    if (!visible) return;

    const key = `${String(this.grid.cols)}x${String(this.grid.rows)}`;
    if (key === this.gridOverlayKey) return;
    this.gridOverlayKey = key;

    const hw = halfWidth(SYNTY_TILE);
    const hh = halfHeight(SYNTY_TILE);
    const graphics = this.gridOverlay;
    graphics.clear();

    /*
      On trace les LIGNES de la grille, pas le contour de chaque case : deux cases
      voisines partagent une arête, et la dessiner deux fois double son épaisseur
      apparente — un quadrillage qui paraît sale sans qu'on sache pourquoi.

      D'où deux familles de segments, chacune parcourue une seule fois : les `cols + 1`
      diagonales à col constant, et les `rows + 1` à row constant.
    */
    /*
      `cellToWorld` rend le CENTRE d'un losange — l'ancre des tuiles vaut 0,421875,
      soit exactement le milieu du losange dans le PNG de 512. Un point de TREILLIS est
      donc le centre décalé d'une demi-hauteur vers le haut : sans ce `− hh`, tout le
      quadrillage flotte d'un quart de case au-dessus du sol.
    */
    const corner = (col: number, row: number): { x: number; y: number } => ({
      x: (col - row) * hw,
      y: (col + row) * hh - hh,
    });

    for (let col = 0; col <= this.grid.cols; col++) {
      const from = corner(col, 0);
      const to = corner(col, this.grid.rows);
      graphics.moveTo(from.x, from.y).lineTo(to.x, to.y);
    }
    for (let row = 0; row <= this.grid.rows; row++) {
      const from = corner(0, row);
      const to = corner(this.grid.cols, row);
      graphics.moveTo(from.x, from.y).lineTo(to.x, to.y);
    }

    // Épaisseur en unités MONDE : le zoom l'amincit à mesure qu'on recule, ce qui est
    // le comportement voulu — de près on compose case par case, de loin on cadre.
    graphics.stroke({ width: 2, color: 0xffffff, alpha: 0.12 });
  }

  /**
   * Peint l'empreinte au sol sous le fantôme, verte si posable et rouge sinon.
   *
   * ── Pourquoi une empreinte AU SOL et pas seulement une teinte sur le bâtiment ──
   *
   * Une teinte verte ou rouge répond à « est-ce posable ? » mais pas à « **où** ça se
   * pose ? ». Or c'est la seconde question qui compte pour un bâtiment 2 × 2, dont le
   * sprite déborde largement de son emprise vers le haut : le joueur croit occuper ce
   * qu'il voit. L'empreinte dit la vérité — exactement les cases prises, et pas une de
   * plus.
   *
   * Elle est dessinée en coordonnées LOCALES puis positionnée, plutôt que retracée à
   * chaque case : un `Graphics.clear()` suivi de quatre `lineTo` à chaque `pointermove`
   * reconstruirait la géométrie soixante fois par seconde pour un losange identique.
   */
  private paintFootprint(type: string, cell: GridCell | null, valid: boolean): void {
    const mark = this.footprintMark;
    if (cell === null || type === '') {
      mark.visible = false;
      return;
    }

    const size = footprintOf(type);
    const key = `${String(size)}:${String(valid)}`;
    if (this.footprintMarkKey !== key) {
      this.footprintMarkKey = key;
      const hw = halfWidth(SYNTY_TILE) * size;
      const hh = halfHeight(SYNTY_TILE) * size;
      mark.clear();
      mark
        .moveTo(0, -hh)
        .lineTo(hw, 0)
        .lineTo(0, hh)
        .lineTo(-hw, 0)
        .closePath()
        .fill({ color: valid ? 0x4ade80 : 0xf87171, alpha: 0.28 })
        .stroke({ width: 3, color: valid ? 0x22c55e : 0xef4444, alpha: 0.9 });
    }

    // EXACTEMENT le centre que prendra le bâtiment : la même fonction, pas une copie.
    // Deux calculs de centrage finissent toujours par diverger, et l'empreinte
    // mentirait alors sur ce qui va se poser.
    const position = this.footprintCenter(type, cell);
    mark.visible = true;
    mark.position.set(position.x, position.y);
    mark.zIndex = this.footprintDepth(type, cell) * 2 + 0.5;
  }

  /** Déplace le fantôme sur une case et signale si elle est jouable. */
  moveGhostTo(cell: GridCell | null, valid: boolean): void {
    const ghost = this.ghost;
    if (ghost === null) return;

    // L'écart des voisins ne se recalcule qu'au CHANGEMENT de case : `pointermove`
    // appelle cette méthode à chaque événement, et relancer les mêmes tweens ferait
    // trembler ce qui doit glisser.
    // Les cases sont toujours ≥ 0 : −1 encode « aucune » sans branche à part.
    const cellChanged =
      (cell?.col ?? -1) !== (this.hoveredCell?.col ?? -1) ||
      (cell?.row ?? -1) !== (this.hoveredCell?.row ?? -1);

    if (cell === null) {
      ghost.visible = false;
      this.paintFootprint('', null, false);
      this.hoveredCell = null;
      if (cellChanged) this.updateLeans('', null);
      return;
    }

    // Le fantôme se centre sur l'emprise, comme le fera le bâtiment posé : sinon
    // l'aperçu ment et le joueur découvre le décalage après coup.
    const type = this.ghostType ?? '';
    const position = this.footprintCenter(type, cell);

    /*
      MÊME MODÈLE que le bâtiment qui sera posé.

      L'orientation et la variante sont dérivées de la POSITION (`orientationFor`,
      `variantFor` de `core`), donc elles changent à chaque case survolée. Le fantôme
      les recalcule et échange sa texture — depuis le cache préchargé par `setGhost`,
      donc sans `await` ni clignotement. Les mêmes fonctions pures que le placement
      sont appelées : impossible que l'aperçu et la pose divergent.
    */
    const name = CityRenderer.spriteName(
      type,
      this.ghostLevel,
      orientationFor(cell.col, cell.row),
      variantFor(type, cell.col, cell.row),
    );
    const wanted = this.buildingTextures.get(name);
    if (wanted !== undefined && ghost.texture !== wanted) {
      ghost.texture = wanted;
      // Une variante est un AUTRE modèle, donc une autre hauteur d'image : l'ancre et
      // l'échelle se recalculent, sinon l'aperçu flotte au-dessus de sa case.
      CityRenderer.applyBuildingFrame(ghost, type);
    }

    ghost.visible = true;
    ghost.position.set(position.x, position.y);
    ghost.zIndex = this.footprintDepth(type, cell) * 2 + 1;
    /*
      La teinte s'est ALLÉGÉE le jour où l'empreinte au sol est arrivée.

      Verte ou rouge à pleine force, elle répondait bien à « est-ce posable ? » — mais
      elle repeignait aussi le bâtiment, donc le joueur ne voyait plus ce qu'il posait.
      Le signal vit désormais au sol, là où il dit AUSSI *où* ça se pose ; la teinte ne
      fait que l'appuyer, assez pour rester lisible d'un coup d'œil et assez peu pour
      que les couleurs du modèle survivent.
    */
    ghost.tint = valid ? 0xd6ffe0 : 0xffc9c4;
    this.paintFootprint(type, cell, valid);
    this.hoveredCell = cell;
    if (cellChanged) this.updateLeans(type, cell);
  }

  getHoveredCell(): GridCell | null {
    return this.hoveredCell;
  }

  /* ── Fantôme de décoration (ROADMAP 2.9a) ────────────────────────────────── */

  private decorGhost: Sprite | null = null;
  private decorGhostKind: string | null = null;
  private decorGhostGeneration = 0;

  /**
   * Arme le fantôme d'une décoration — le même geste de visée que les bâtiments,
   * pour un objet d'une case.
   *
   * La texture passe par `traceSpriteFor`, la MÊME résolution que `syncTraces` : le
   * fantôme montre exactement ce que la pose créera, deux chemins ne peuvent pas
   * diverger. Jeton de génération comme le fantôme de bâtiment : une annulation
   * pendant le chargement ne doit pas ressusciter un aperçu que plus personne ne
   * pilote (P4.2).
   */
  async setDecorGhost(kind: string | null): Promise<void> {
    this.decorGhost?.destroy();
    this.decorGhost = null;
    this.decorGhostKind = kind;
    if (kind === null) {
      this.paintFootprint('', null, false);
      return;
    }

    const name = traceSpriteFor(kind);
    if (name === null) return;

    const generation = ++this.decorGhostGeneration;
    let texture = this.propTextures.get(name);
    if (texture === undefined) {
      texture = await cityTexture(name);
      this.propTextures.set(name, texture);
    }
    if (generation !== this.decorGhostGeneration || this.decorGhostKind !== kind) return;

    const sprite = new Sprite(texture);
    sprite.anchor.set(0.5, 0.5);
    sprite.scale.set(assetScale(name) ?? 0.72);
    sprite.alpha = 0.75;
    sprite.zIndex = 100_000;
    // Créé INVISIBLE, comme le fantôme de bâtiment : sans première case visée, il
    // surgirait en (0,0) avant le premier toucher.
    sprite.visible = false;
    this.decorGhost = sprite;
    this.tileLayer.addChild(sprite);
  }

  getDecorGhostKind(): string | null {
    return this.decorGhostKind;
  }

  /** Déplace le fantôme de décoration et signale si la case est jouable. */
  moveDecorGhostTo(cell: GridCell | null, valid: boolean): void {
    const ghost = this.decorGhost;
    if (ghost === null) return;

    if (cell === null) {
      ghost.visible = false;
      this.paintFootprint('', null, false);
      return;
    }

    const position = cellToWorld(cell, SYNTY_TILE);
    ghost.visible = true;
    ghost.position.set(position.x, position.y);
    // Même profondeur qu'aura la trace posée (`syncTraces` : depth × 2 + 0,9) — un
    // aperçu trié autrement montrerait une occlusion que la pose contredirait.
    ghost.zIndex = depthOf(cell) * 2 + 0.9;
    ghost.tint = valid ? 0xd6ffe0 : 0xffc9c4;
    // `footprintOf` d'une sorte inconnue du catalogue de bâtiments vaut 1 : l'empreinte
    // au sol d'une décoration est sa case, exactement.
    this.paintFootprint(this.decorGhostKind ?? '', cell, valid);
  }

  /* ── B.2 — les voisins s'écartent du fantôme ─────────────────────────────── */

  /**
   * Écarts en cours : pour chaque sprite, sa position de repos et son décalage courant.
   * La position de REPOS est mémorisée à l'entrée dans l'effet — c'est elle qu'on
   * restitue, jamais une position recalculée qui pourrait diverger d'un demi-pixel.
   */
  private readonly leans = new Map<
    Sprite,
    { baseX: number; baseY: number; dx: number; dy: number; handle: { cancel(): void } }
  >();

  /**
   * Recalcule qui s'écarte, à chaque changement de case survolée.
   *
   * Les voisins se trouvent par DISTANCE aux sprites, pas par lecture de l'état de
   * jeu : le renderer connaît les positions monde, et c'est tout ce que l'effet
   * demande. Un rayon en tuiles autour du centre de l'emprise suffit — l'effet est
   * un souffle, pas une règle, et un faux positif d'un demi-rayon est invisible.
   */
  private updateLeans(type: string, cell: GridCell | null): void {
    const { leanPx, leanMs, leanRadiusTiles } = JUICE.placement;

    const wanted = new Map<Sprite, { dx: number; dy: number }>();
    if (cell !== null) {
      const center = this.footprintCenter(type, cell);
      const radius = SYNTY_TILE.width * 0.5 * (footprintOf(type) + leanRadiusTiles);

      for (const sprite of this.buildingSprites.values()) {
        if (isGone(sprite)) continue;
        const currentLean = this.leans.get(sprite);
        // La distance se mesure depuis la position de REPOS : un sprite déjà écarté
        // mesuré sur sa position décalée entrerait et sortirait du rayon en boucle.
        const restX = currentLean?.baseX ?? sprite.x;
        const restY = currentLean?.baseY ?? sprite.y;
        const dx = restX - center.x;
        const dy = restY - center.y;
        const distance = Math.hypot(dx, dy);
        if (distance === 0 || distance > radius) continue;

        // L'écart suit le sol isométrique : la composante verticale est aplatie au
        // rapport du losange, sinon les voisins du haut « sautent » au lieu de glisser.
        const scale = leanPx / distance;
        wanted.set(sprite, { dx: dx * scale, dy: dy * scale * 0.566 });
      }
    }

    // Ceux qui n'ont plus lieu de s'écarter reviennent à leur place.
    for (const [sprite, lean] of [...this.leans]) {
      if (!wanted.has(sprite)) this.leanTo(sprite, lean.baseX, lean.baseY, 0, 0, leanMs, true);
    }
    // Les nouveaux (ou ceux dont la direction change) glissent vers leur écart.
    for (const [sprite, target] of wanted) {
      const lean = this.leans.get(sprite);
      const baseX = lean?.baseX ?? sprite.x;
      const baseY = lean?.baseY ?? sprite.y;
      this.leanTo(sprite, baseX, baseY, target.dx, target.dy, leanMs, false);
    }
  }

  /** Glisse un sprite de son décalage courant vers `(dx, dy)` autour de sa base. */
  private leanTo(
    sprite: Sprite,
    baseX: number,
    baseY: number,
    dx: number,
    dy: number,
    durationMs: number,
    release: boolean,
  ): void {
    this.leans.get(sprite)?.handle.cancel();

    const fromDx = sprite.destroyed ? 0 : sprite.x - baseX;
    const fromDy = sprite.destroyed ? 0 : sprite.y - baseY;

    const handle = this.tweens.add(
      {
        from: 0,
        to: 1,
        durationMs,
        easing: 'out',
        onUpdate: (t) => {
          if (isGone(sprite)) return;
          sprite.position.set(
            baseX + fromDx + (dx - fromDx) * t,
            baseY + fromDy + (dy - fromDy) * t,
          );
        },
        onComplete: () => {
          if (release) {
            if (!isGone(sprite)) sprite.position.set(baseX, baseY);
            this.leans.delete(sprite);
          }
        },
      },
      performance.now(),
    );

    this.leans.set(sprite, { baseX, baseY, dx, dy, handle });
  }

  /** Nombre de voisins actuellement écartés — lu par les sondes `?debug`. */
  get leaningCount(): number {
    return this.leans.size;
  }

  /* ── B.6 — la pluie de pièces du retour ──────────────────────────────────── */

  /** Pièces actuellement en vol — lu par les sondes `?debug`. */
  get coinsInFlight(): number {
    return this.coinFlight?.inFlight ?? 0;
  }

  /**
   * Fait pleuvoir des pièces depuis le haut de l'écran vers le compteur (B.6).
   *
   * Le nombre suit le LOG du gain (`rainCount`), les départs s'espacent sous le
   * plafond de débit du vol de pièces, et chaque pièce est `quiet` : le gain est déjà
   * annoncé par l'écran de retour, vingt étiquettes seraient du bruit.
   */
  rainCoins(total: number, now: number): void {
    if (this.coinFlight === null || total <= 0) return;
    const count = rainCount(total);

    for (let i = 0; i < count; i++) {
      this.tweens.add(
        {
          from: 0,
          to: 1,
          durationMs: 1,
          delayMs: i * JUICE.returnRain.staggerMs,
          onUpdate: () => undefined,
          onComplete: () => {
            this.coinFlight?.emit(
              // Départ au-dessus du bord haut, réparti sur la largeur — une pluie.
              { x: Math.random() * this.viewport.width, y: -24 },
              0,
              performance.now(),
              true,
            );
          },
        },
        now,
      );
    }
  }

  /* ── B.5 — secousse d'écran dosée ────────────────────────────────────────── */

  /** Trace de la dernière secousse — lue par les sondes `?debug`. */
  lastShake: { amplitudePx: number; at: number } | null = null;

  private shakeHandle: { cancel(): void } | null = null;

  /**
   * Secoue l'écran — la SCÈNE entière, HUD exclu (il vit en HTML au-dessus).
   *
   * Réservée aux événements rares (B.5) : prestige, premier jalon de la session,
   * extension de terrain. La responsabilité de la rareté appartient aux appelants ;
   * cette méthode, elle, garantit qu'une secousse ne s'additionne jamais à une autre
   * — la dernière remplace la précédente, et l'écran revient toujours à (0, 0).
   */
  shakeScreen(amplitudePx: number, durationMs: number, now: number = performance.now()): void {
    this.shakeHandle?.cancel();
    this.app.stage.position.set(0, 0);
    this.lastShake = { amplitudePx, at: now };

    const { oscillations } = JUICE.shake;
    // Phase tirée : deux secousses successives ne dessinent pas le même trajet.
    const phase = Math.random() * Math.PI * 2;

    this.shakeHandle = this.tweens.add(
      {
        from: 0,
        to: 1,
        durationMs,
        easing: 'out',
        onUpdate: (t) => {
          // Oscillation amortie : l'énergie part d'un coup et s'éteint, comme un choc.
          const decay = 1 - t;
          const angle = phase + t * oscillations * Math.PI * 2;
          this.app.stage.position.set(
            Math.cos(angle) * amplitudePx * decay,
            Math.sin(angle * 1.3) * amplitudePx * decay * 0.7,
          );
        },
        onComplete: () => {
          this.app.stage.position.set(0, 0);
          this.shakeHandle = null;
        },
      },
      now,
    );
  }

  /* ── B.3 — célébrations à paliers ────────────────────────────────────────── */

  /**
   * Célèbre un jalon : gerbe de particules depuis le centre de la vue (E§1.4).
   * `withConfetti` déclenche EN PLUS la pluie plein écran — l'appelant porte la
   * règle de rareté (première fois de la session), pas le renderer.
   */
  celebrateMilestone(now: number, withConfetti: boolean): void {
    const celebrations = this.celebrations;
    if (celebrations === null) return;
    celebrations.burst({ x: this.viewport.width / 2, y: this.viewport.height * 0.42 }, now);
    if (withConfetti) celebrations.confetti(this.viewport.width, this.viewport.height, now);
  }

  /** Trace du dernier envol de prestige — lue par les sondes `?debug`. */
  lastFarewell: { buildings: number; at: number } | null = null;

  /**
   * L'adieu du prestige : la ville s'envole bâtiment par bâtiment avant le reset
   * (B.3 — « la perte doit être un feu d'artifice, pas un écran blanc »).
   *
   * La durée TOTALE est bornée par `farewellMs`, quel que soit le nombre de
   * bâtiments : les départs se répartissent dans la fenêtre au lieu de s'additionner.
   * Une ville de 60 bâtiments ne fait pas attendre le joueur une minute — c'est un
   * adieu, pas une pénitence. `onDone` reçoit la main quand tout est en l'air :
   * c'est là que l'appelant applique le reset.
   */
  playPrestigeFarewell(now: number, onDone: () => void): void {
    const { farewellMs, farewellRisePx } = JUICE.prestige;
    const sprites = [...this.buildingSprites.values()].filter((sprite) => !isGone(sprite));
    this.lastFarewell = { buildings: sprites.length, at: now };

    if (sprites.length === 0) {
      onDone();
      return;
    }

    const flightMs = Math.min(480, farewellMs * 0.55);
    const window = farewellMs - flightMs;
    let finished = 0;

    sprites.forEach((sprite, index) => {
      const baseY = sprite.y;
      this.tweens.add(
        {
          from: 0,
          to: 1,
          durationMs: flightMs,
          // L'ordre de la Map est l'ordre de pose : la ville part comme elle est venue.
          delayMs: (window * index) / sprites.length,
          easing: 'in',
          onUpdate: (t) => {
            if (isGone(sprite)) return;
            sprite.y = baseY - farewellRisePx * t;
            sprite.alpha = 1 - t;
          },
          onComplete: () => {
            finished++;
            // Un sprite détruit en route (resynchronisation) compte : l'adieu doit
            // TOUJOURS rendre la main, sinon le reset n'arrive jamais.
            if (finished === sprites.length) onDone();
          },
        },
        now,
      );
    });
  }

  /**
   * Ombre portée sous le plateau, en coordonnées écran.
   *
   * Recalculée à chaque frame parce qu'elle suit la caméra, mais elle ne coûte que six
   * ellipses : les redessiner est moins cher que de suivre la caméra par une
   * transformation, et bien plus simple à lire.
   */
  private updatePlateShadow(): void {
    const camera = this.camera;
    const sky = this.sky;
    if (camera === null || sky === null) return;

    const bounds = gridWorldBounds(this.grid, SYNTY_TILE);
    const centre = this.worldToScreen({
      x: (bounds.minX + bounds.maxX) / 2,
      y: (bounds.minY + bounds.maxY) / 2,
    });
    const { zoom } = camera.getState();

    sky.setPlateShadow(
      centre.x,
      // Décalée vers le bas : une ombre centrée sous le plateau ne se verrait pas,
      // le plateau la couvrirait entièrement.
      // Plus bas qu'avant : la sous-face d'île descend sous le plateau, l'ombre doit
      // tomber sous elle et non derrière.
      centre.y + ((bounds.maxY - bounds.minY) / 2) * zoom * 0.62,
      ((bounds.maxX - bounds.minX) / 2) * zoom * 0.92,
      ((bounds.maxY - bounds.minY) / 2) * zoom * 0.34,
    );
  }

  /**
   * Ombre de contact, dessinée une fois en dégradé radial puis partagée.
   *
   * Sans elle, chaque bâtiment paraît collé sur le sol plutôt que posé dessus — c'est
   * ce qui manque en premier à une scène isométrique, avant même les ombres portées.
   * Un dégradé plutôt qu'une ellipse pleine : une ombre à bord net se lit comme une
   * tache de peinture.
   */
  /**
   * Halo chaud d'une fenêtre éclairée.
   *
   * Le dégradé n'est pas linéaire : une lampe vue de loin a un cœur net et une décrue
   * rapide. Un dégradé régulier donne une tache de brouillard, pas une lumière — c'est
   * la même raison qui fait qu'une bougie photographiée n'a pas l'air d'un rond flou.
   *
   * Teinte 45 — l'ambre d'une ampoule domestique. Un blanc pur se lirait comme un
   * projecteur, et la nuit de Villopia est bleue : c'est le CONTRASTE chaud/froid qui
   * fait tout le travail, pas la luminosité.
   */
  private static makeGlowTexture(): Texture {
    const size = 128;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext('2d');
    if (context === null) return Texture.WHITE;

    const gradient = context.createRadialGradient(
      size / 2,
      size / 2,
      0,
      size / 2,
      size / 2,
      size / 2,
    );
    // Un cœur à 92 % saturait en blanc dès qu'on l'ajoutait au décor. La décrue reste
    // rapide — c'est ce qui distingue une lampe d'une tache de brouillard — mais le
    // maximum descend là où l'ambre survit au mélange additif.
    gradient.addColorStop(0, 'rgb(255 206 132 / 72%)');
    gradient.addColorStop(0.25, 'rgb(255 188 104 / 40%)');
    gradient.addColorStop(0.6, 'rgb(255 168 78 / 13%)');
    gradient.addColorStop(1, 'rgb(255 160 70 / 0%)');
    context.fillStyle = gradient;
    context.fillRect(0, 0, size, size);

    return Texture.from(canvas);
  }

  private static makeShadowTexture(renderer: Renderer): Texture {
    const size = 128;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext('2d');
    if (context === null) return Texture.WHITE;

    const gradient = context.createRadialGradient(
      size / 2,
      size / 2,
      0,
      size / 2,
      size / 2,
      size / 2,
    );
    gradient.addColorStop(0, 'rgb(24 30 18 / 46%)');
    gradient.addColorStop(0.55, 'rgb(24 30 18 / 22%)');
    gradient.addColorStop(1, 'rgb(24 30 18 / 0%)');
    context.fillStyle = gradient;
    context.fillRect(0, 0, size, size);

    void renderer;
    return Texture.from(canvas);
  }

  /* ── Routes ──────────────────────────────────── */

  /**
   * Aligne les sprites de route sur le réseau de l'état de jeu.
   *
   * Chaque case relit son masque de connexions : poser une route change la forme de
   * ses VOISINES — une impasse devient un virage, un virage un T. Ne mettre à jour que
   * la case ajoutée laisserait des rues qui ne se rejoignent pas.
   */
  async syncRoads(roads: readonly string[], animateFrom?: readonly string[]): Promise<void> {
    const generation = ++this.roadsGeneration;
    this.traffic?.setNetwork(roads);
    const network = new Set(roads);
    const animated = new Set(animateFrom ?? []);

    for (const [key, sprite] of this.roadSprites) {
      if (!network.has(key)) {
        // Le tween de déroulement peut être encore en cours (260 ms + 90 ms par case) :
        // on l'annule AVANT de détruire, sinon il continuerait d'écrire dans le sprite.
        // La garde `whileAlive` couvre le cas, l'annulation évite le travail inutile.
        this.roadTweens.get(key)?.cancel();
        this.roadTweens.delete(key);
        this.tileLayer.removeChild(sprite);
        sprite.destroy();
        this.roadSprites.delete(key);
      }
    }

    for (const key of roads) {
      const cell = parseRoadKey(key);
      const { shape, rotation } = roadSprite(connectionMask(network, cell.col, cell.row));
      const name = `${shape}${rotation === 0 ? '' : `-r${String(rotation)}`}.png`;

      let texture = this.roadTextures.get(name);
      if (texture === undefined) {
        texture = await cityTexture(name);
        if (generation !== this.roadsGeneration) return;
        this.roadTextures.set(name, texture);
      }

      const view = cell;
      const position = cellToWorld(view, SYNTY_TILE);
      // Juste au-dessus de sa tuile, sous tout ce qui se pose dessus.
      const depth = depthOf(view) * 2 + 0.5;

      const existing = this.roadSprites.get(key);
      if (existing !== undefined && !existing.destroyed) {
        /*
          Même case, forme éventuellement différente : on remplace la texture plutôt que
          le sprite, pour ne pas relancer l'animation d'une route déjà en place.

          Mais la position et la profondeur se recalculent AUSSI. La branche se
          contentait de la texture puis passait au suivant : après un pivot, les routes
          restaient exactement où elles étaient — le réseau flottait en diagonale au
          milieu de l'herbe, détaché des maisons (mesuré : 0 route déplacée sur 1).
        */
        existing.texture = texture;
        existing.position.set(position.x, position.y);
        existing.zIndex = depth;
        continue;
      }

      const sprite = new Sprite(texture);
      sprite.anchor.set(0.5, 0.5);
      sprite.position.set(position.x, position.y);
      sprite.zIndex = depth;

      this.roadSprites.set(key, sprite);
      this.tileLayer.addChild(sprite);

      if (animated.has(key)) {
        // Déroulement : la route pousse depuis le bâtiment vers le réseau. Le décalage
        // suit l'ordre du tracé, ce qui donne le sens de progression.
        const order = (animateFrom ?? []).indexOf(key);
        sprite.alpha = 0;
        sprite.scale.set(0.55);
        const handle = this.tweens.add(
          {
            from: 0,
            to: 1,
            durationMs: 260,
            easing: 'back',
            delayMs: Math.max(0, order) * 90,
            // `whileAlive` : vendre le bâtiment pendant le déroulement élague la route,
            // détruit le sprite, et ce `onUpdate` écrivait dans un objet libéré — une
            // TypeError DANS le ticker, donc le rendu entier figé (audit P0.1).
            onUpdate: whileAlive(sprite, (t: number) => {
              sprite.alpha = Math.min(1, t * 1.4);
              sprite.scale.set(0.55 + 0.45 * t);
            }),
            onComplete: () => {
              this.roadTweens.delete(key);
            },
          },
          performance.now(),
        );
        this.roadTweens.set(key, handle);
      }
    }
  }

  /**
   * Oublie le sprite d'un bâtiment pour forcer sa recréation.
   *
   * Nécessaire quand le MODÈLE change — amélioration de palier, rotation, variante —
   * puisque `syncBuildings` ne recrée que ce qu'il ne connaît pas. Sans cela, un
   * bâtiment amélioré garderait l'apparence de son ancien palier tout en produisant
   * comme le nouveau, ce qui est le pire des deux mondes.
   */
  /**
   * Rend TRANSPARENT ce qui masque le bâtiment sur lequel le joueur travaille.
   *
   * ══ Le défaut ═══════════════════════════════════════════════════════════════
   *
   * En vue isométrique, ce qui est devant recouvre ce qui est derrière — c'est le tri
   * en profondeur qui fait la ville. Mais quand le joueur SÉLECTIONNE un bâtiment, ce
   * même tri lui cache exactement ce qu'il manipule : il ouvre le panneau d'un cabinet
   * médical et voit une tour. Spécifié depuis longtemps (E§), jamais fait.
   *
   * ══ Pourquoi on mesure le CHEVAUCHEMENT et pas la distance ══════════════════
   *
   * La tentation est de fondre « les voisins proches ». Ça se règle mal : trop peu et
   * la tour reste opaque, trop et la moitié de la ville clignote à chaque tap. Or la
   * question n'est pas « qui est près ? » mais « qui cache ? », et elle a une réponse
   * exacte — deux boîtes à l'écran se croisent, ou non.
   *
   * On la pose donc telle quelle : est occultant tout sprite dessiné APRÈS la cible
   * (donc devant elle) dont les bornes croisent les siennes. Aucun réglage, et le
   * résultat est juste au pixel quel que soit le zoom, l'emprise ou la hauteur du
   * modèle — une tour de six losanges masque de loin, une échoppe ne masque personne.
   *
   * `null` rétablit tout le monde. L'appelant n'a rien à retenir : la couche se souvient
   * de qui elle a fondu.
   */
  setFocus(buildingId: string | null): void {
    const target = buildingId === null ? undefined : this.buildingSprites.get(buildingId);

    // Rétablir d'abord, toujours : un focus qui se déplace laisserait sinon derrière lui
    // les fantômes du précédent, et deux ou trois sélections suffiraient à dépeupler la
    // ville. On ne suit pas « qui était fondu », on remet tout et on refond.
    for (const sprite of this.buildingSprites.values()) {
      if (!isGone(sprite)) sprite.alpha = 1;
    }

    if (target === undefined || isGone(target) || !target.visible) return;

    const focus = target.getBounds();

    for (const [id, sprite] of this.buildingSprites) {
      if (id === buildingId || isGone(sprite) || !sprite.visible) continue;
      // Dessiné AVANT la cible : il est derrière, il ne peut rien masquer.
      if (sprite.zIndex <= target.zIndex) continue;

      const other = sprite.getBounds();
      const overlaps =
        other.x < focus.x + focus.width &&
        other.x + other.width > focus.x &&
        other.y < focus.y + focus.height &&
        other.y + other.height > focus.y;

      // Pas invisible : à zéro, le joueur perdrait de vue ce qu'il est en train de
      // contourner, et le sentiment de ville avec. On voit au travers, on ne l'efface pas.
      if (overlaps) sprite.alpha = CityRenderer.OCCLUDER_ALPHA;
    }
  }

  forget(buildingId: string): void {
    const sprite = this.buildingSprites.get(buildingId);
    if (sprite !== undefined) {
      this.tileLayer.removeChild(sprite);
      if (!sprite.destroyed) sprite.destroy();
      this.buildingSprites.delete(buildingId);
    }

    const shadow = this.shadowSprites.get(buildingId);
    if (shadow !== undefined) {
      this.tileLayer.removeChild(shadow);
      if (!shadow.destroyed) shadow.destroy();
      this.shadowSprites.delete(buildingId);
    }

    /*
      ⚠️ La LUEUR part avec le bâtiment. Oubliée ici, elle restait orpheline dans le
      calque à son ancienne position — et `syncBuildings` en recréait une par-dessus
      en ÉCRASANT l'entrée de la carte, donc plus personne ne pouvait la retirer.
      Chaque déplacement, amélioration ou pivot laissait une lumière fantôme :
      mesuré 8 orphelines après un seul pivot de 8 maisons — « aucune lumière ne
      s'allume au bon endroit » (Benoit, 18 août).
    */
    const glow = this.glowSprites.get(buildingId);
    if (glow !== undefined) {
      this.tileLayer.removeChild(glow.sprite);
      if (!glow.sprite.destroyed) glow.sprite.destroy();
      this.glowSprites.delete(buildingId);
    }
  }

  /**
   * Éclairage nocturne de la ville.
   *
   * Deux effets combinés, et c'est leur combinaison qui rend la nuit LISIBLE plutôt que
   * simplement sombre :
   *
   * · les bâtiments prennent une teinte froide, plus marquée sur les surfaces claires,
   *   ce qui les fait reculer sans les noyer ;
   * · une lueur chaude est ajoutée par-dessus, en fondu additif — ce sont les fenêtres
   *   allumées. Sans elle, les tours vitrées deviennent des rectangles noirs
   *   indiscernables les uns des autres, ce qui est précisément le défaut à corriger.
   *
   * La teinte s'applique au CONTENEUR et non sprite par sprite : une passe de plus par
   * bâtiment coûterait à chaque frame, pour un résultat identique.
   */
  /**
   * Décalage d'horloge du cycle jour/nuit, en millisecondes. Zéro en production.
   *
   * ══ Pourquoi ce réglage existe ══════════════════════════════════════════════
   *
   * Le cycle dure six minutes de vrai temps. Vérifier que les fenêtres s'allument
   * demandait donc soit d'attendre, soit de forcer `applyNightLighting` de l'extérieur —
   * ce que la sonde a d'abord fait, et qui **ne marche pas** : la boucle de rendu
   * réappelle la fonction avec l'heure réelle à la frame suivante, écrasant la valeur
   * forcée en seize millisecondes. La sonde mesurait alors le hasard de l'heure qu'il
   * était.
   *
   * Un décalage lu par la boucle elle-même est la seule façon d'avancer le cycle sans
   * mentir sur ce qu'il produit : le ciel ET l'éclairage suivent le même temps, comme en
   * jeu. Il n'est manipulé que par la sonde `?debug`.
   */
  private clockOffsetMs = 0;

  /**
   * Opacité maximale d'un halo de fenêtre.
   *
   * Mesurée contre le défaut, pas choisie : à pleine opacité, le mélange additif sature
   * en blanc et le bâtiment disparaît dans sa propre lumière. On éclaire une façade, on
   * ne l'efface pas.
   */
  /*
    0,42 à la première passe : les taches blanches disparaissaient — et les lampes
    aussi. La capture de contrôle montrait une ville nocturne où l'on DEVINAIT les
    fenêtres au lieu de les voir. 0,68 est le point où l'effet se voit franchement
    pendant que la sonde compte toujours zéro pixel brûlé : c'est elle qui borne, pas
    la prudence.
  */
  // 0,68 → 0,85 : « beaucoup plus d'éclairage » (18 août). Le plafond reste sous 1
  // — au-delà, on n'éclaire plus une façade, on l'efface.
  /** Opacité d'un bâtiment qui masque celui qu'on manipule (ROADMAP 1.8). */
  private static readonly OCCLUDER_ALPHA = 0.28;

  private static readonly GLOW_MAX_ALPHA = 0.85;

  /**
   * Heure du jour FIGÉE, de 0 à 1 — ou `null` pour suivre l'horloge réelle.
   *
   * C.3, la page visiteur : « l'heure dorée forcée ». Un décalage ne suffirait pas —
   * le cycle dure six minutes, et une visite de trente secondes ferait glisser le
   * couchant vers la nuit. La vitrine tient son heure ; le jeu, lui, vit.
   */
  private heldPhase: number | null = null;

  holdDayPhase(phase: number): void {
    this.heldPhase = Math.min(1, Math.max(0, phase));
    this.lastNight = -1;
    this.applyNightLighting(this.worldClock());
  }

  /** Heure figée courante, ou `null` — lu par la sonde de la page visiteur. */
  getHeldDayPhase(): number | null {
    return this.heldPhase;
  }

  /** Heure du monde : réelle plus le décalage de sonde, ou l'heure figée (vitrine). */
  private worldClock(): number {
    if (this.heldPhase !== null) return this.heldPhase * DAY_LENGTH_MS;
    return Date.now() + this.clockOffsetMs;
  }

  /** Densité du trafic — C.3, la vitrine visiteur montre une ville qui vit. */
  boostTrafficDensity(multiplier: number): void {
    this.traffic?.setDensityBoost(multiplier);
  }

  /** Avance ou recule le cycle jour/nuit. Réservé aux sondes (`?debug`). */
  /**
   * Ère affichée — elle décale la teinte du ciel (ROADMAP 3.9).
   *
   * Le renderer ne LIT pas le snapshot : le jeu le lui dit. C'est la même frontière que
   * partout ailleurs ici, et c'est elle qui permet à la page publique de montrer l'ère du
   * PROPRIÉTAIRE de la ville plutôt que celle du visiteur.
   */
  setEra(era: number): void {
    this.sky?.setEra(era);
  }

  setClockOffset(ms: number): void {
    this.clockOffsetMs = ms;
    // Le garde de `applyNightLighting` ignore les variations infimes : sauter dans le
    // cycle doit repeindre, même si la nuit tombe au même endroit qu'avant.
    this.lastNight = -1;
    this.applyNightLighting(this.worldClock());
  }

  private applyNightLighting(now: number): void {
    const { stars } = skyAt(now);
    if (Math.abs(stars - this.lastNight) < 0.01) return;
    this.lastNight = stars;

    // Bleu nuit progressif : jamais noir, le joueur doit continuer à reconnaître sa ville.
    const level = 1 - stars * 0.34;
    const blue = 1 - stars * 0.14;
    const tint =
      (Math.round(255 * level) << 16) | (Math.round(255 * level) << 8) | Math.round(255 * blue);

    this.tileLayer.tint = tint;
    this.applyWindowGlow(stars);
  }

  /**
   * Allume les fenêtres, bâtiment par bâtiment.
   *
   * Appelée uniquement quand la nuit a bougé d'au moins 1 % (le garde de
   * `applyNightLighting`) : sur un cycle de plusieurs minutes, cela fait quelques
   * dizaines de passes par jour de jeu, pas une par frame.
   *
   * ⚠️ **Le halo échappe à la teinte de nuit.** Les lueurs vivent dans `tileLayer`, dont
   * la teinte assombrit tout ce qu'il contient — y compris ce qui est censé éclairer. On
   * compense en teintant chaque halo de l'inverse exact : sans ça, plus la nuit tombe,
   * plus les lampes faiblissent, ce qui est le contraire de l'effet voulu.
   */
  private applyWindowGlow(stars: number): void {
    if (this.glowSprites.size === 0) return;

    const worldNow = this.worldClock();
    for (const glow of this.glowSprites.values()) {
      // L'enveloppe de nuit × la vie derrière la fenêtre : l'une dit « il fait
      // assez noir pour allumer », l'autre « quelqu'un est là en ce moment ».
      const intensity =
        windowGlowAt(glow.col, glow.row, stars) * windowLitGate(glow.col, glow.row, worldNow);
      glow.sprite.visible = intensity > 0.01;
      /*
        ⚠️ Plafond d'opacité, et **plus aucune compensation de teinte**.

        La première version teintait chaque halo de l'inverse exact de la teinte de nuit,
        pour qu'il « échappe » à l'assombrissement du calque. Résultat : à faible nuit la
        compensation vaut presque du blanc pur, et un blanc additif à pleine opacité
        SATURE — l'ambre disparaissait, il ne restait qu'une tache blanche.

        Un mélange additif n'a pas besoin de ça : il AJOUTE au fond au lieu de le
        remplacer, donc la teinte du calque ne peut pas l'éteindre. On laisse donc
        l'ambre de la texture intact, et on borne l'opacité — au-delà, on n'éclaire plus
        une façade, on l'efface.
      */
      glow.sprite.alpha = intensity * CityRenderer.GLOW_MAX_ALPHA;
    }
  }

  /* ── Décor ambiant ─────────────────────────────────────────────────── */

  /**
   * Éparpille de la végétation sur les cases libres.
   *
   * Une île à moitié vide n'est pas un problème d'équilibrage, c'est un problème de
   * décor : sans rien dessus, l'espace non bâti se lit comme « pas encore fait ». Les
   * props le transforment en campagne — et rendent visible la conquête du terrain à
   * mesure que la ville s'étend.
   *
   * Le choix est DÉTERMINISTE : deux chargements donnent la même campagne, et un
   * arbre ne saute pas d'une case à l'autre au rechargement. Même principe que
   * l'orientation et les variantes.
   */
  /* ── Traces des visiteurs — ENTITÉS (0.36.0) ─────────────────────────────── */

  /*
    La correspondance sorte → sprite vit dans `config/decorations.ts`
    (`traceSpriteFor`), DÉRIVÉE du catalogue. La table manuscrite qui vivait ici ne
    connaissait que quatre sortes : neuf des douze décorations achetables étaient
    invisibles après achat — payées, dans l'état, jamais rendues (règle 15).
  */

  /** Sprites des traces, par identifiant d'entité. */
  private readonly traceSprites = new Map<string, Sprite>();

  /** Inventaire du décor posé (ambiance + traces) — lu par les sondes `?debug`. */
  propInventory(): Record<string, number> {
    const inventory: Record<string, number> = {};
    for (const name of this.propNames.values()) {
      const base = name.replace(/-r[123]$/, '');
      inventory[base] = (inventory[base] ?? 0) + 1;
    }
    for (const sprite of this.traceSprites.values()) {
      if (sprite.destroyed) continue;
      const base = sprite.label;
      inventory[base] = (inventory[base] ?? 0) + 1;
    }
    return inventory;
  }

  /**
   * Modèle de décor D'AMBIANCE posé sur une case, ou `null` — lu par les sondes.
   *
   * Il ne dit rien des traces, qui vivent dans une autre passe : c'est exactement ce
   * qui permet de vérifier que le décor d'ambiance **cède sa case** à une entité au
   * lieu de venir se poser dessus.
   */
  propNameAt(col: number, row: number): string | null {
    return this.propNames.get(cellKey({ col, row })) ?? null;
  }

  /** Identifiant de la trace posée sur une case, ou `null` — pour le tap du joueur. */
  traceAt(col: number, row: number): string | null {
    for (const [id, sprite] of this.traceSprites) {
      if (sprite.destroyed) continue;
      const at = this.traceCells.get(id);
      if (at?.col === col && at.row === row) return id;
    }
    return null;
  }

  private readonly traceCells = new Map<string, { col: number; row: number }>();

  /**
   * Aligne les sprites de traces sur l'état — même contrat que `syncBuildings` :
   * l'état fait foi, un sprite détruit vaut absent, et seules les NOUVELLES traces
   * reçoivent la fanfare (`animateNew`) — on célèbre l'événement, jamais l'état.
   */
  async syncTraces(
    traces: readonly { id: string; kind: string; col: number; row: number }[],
    animateNew: boolean,
  ): Promise<void> {
    const seen = new Set<string>();

    for (const trace of traces) {
      seen.add(trace.id);
      this.traceCells.set(trace.id, { col: trace.col, row: trace.row });
      const name = traceSpriteFor(trace.kind);
      if (name === null) continue;

      const cell = { col: trace.col, row: trace.row };
      const position = cellToWorld(cell, SYNTY_TILE);
      const depth = depthOf(cell) * 2 + 0.9;

      const existing = this.traceSprites.get(trace.id);
      if (existing !== undefined && !existing.destroyed) {
        existing.position.set(position.x, position.y);
        existing.zIndex = depth;
        continue;
      }

      let texture = this.propTextures.get(name);
      if (texture === undefined) {
        texture = await cityTexture(name);
        this.propTextures.set(name, texture);
      }

      const sprite = new Sprite(texture);
      sprite.label = trace.kind;
      sprite.anchor.set(0.5, 0.5);
      sprite.position.set(position.x, position.y);
      sprite.scale.set(assetScale(name) ?? 0.72);
      sprite.zIndex = depth;
      this.traceSprites.set(trace.id, sprite);
      this.tileLayer.addChild(sprite);

      if (animateNew) {
        // La fanfare : la trace TOMBE du ciel et rebondit — l'arrivée d'un visiteur
        // se voit au moment où elle se produit (demande de Benoit).
        const restY = position.y;
        const restScale = sprite.scale.x;
        sprite.y = restY - 46;
        sprite.alpha = 0;
        this.tweens.add(
          {
            from: 0,
            to: 1,
            durationMs: 420,
            easing: 'back',
            onUpdate: (t) => {
              if (isGone(sprite)) return;
              sprite.y = restY - 46 * (1 - t);
              sprite.alpha = Math.min(1, t * 2);
              sprite.scale.set(restScale * (0.7 + 0.3 * t));
            },
            onComplete: () => {
              if (isGone(sprite)) return;
              sprite.y = restY;
              sprite.scale.set(restScale);
            },
          },
          performance.now(),
        );
        this.celebrations?.burst(this.worldToScreen(position), performance.now());
      }
    }

    for (const [id, sprite] of this.traceSprites) {
      if (seen.has(id)) continue;
      if (!sprite.destroyed) {
        this.tileLayer.removeChild(sprite);
        sprite.destroy();
      }
      this.traceSprites.delete(id);
      this.traceCells.delete(id);
    }
  }

  /**
   * Cases de décor portant un FEU → carrefour qu'il règle.
   *
   * Reconstruite à chaque `syncProps` ; `updateTrafficLights` la relit par frame pour
   * colorer la lampe. Une entrée dont le sprite a disparu est simplement ignorée.
   */
  private readonly lightJunctions = new Map<string, GridCell>();

  async syncProps(
    occupied: ReadonlySet<string>,
    roads?: ReadonlySet<string>,
    built?: ReadonlySet<string>,
  ): Promise<void> {
    const generation = ++this.propsGeneration;
    const wanted = new Map<string, string>();
    const network = roads ?? new Set<string>();

    /*
      ══ LE MOBILIER URBAIN VIT SUR LA ROUTE, PAS SUR LA CASE VOISINE ═══════════

      Demande explicite de Benoit (18 août) : « je veux pas que les lampadaires
      disparaissent ! je veux que ce soit collé à la route et que ça reste ! »

      L'ancienne version posait lampadaires, bancs et signalisation sur la case
      RIVERAINE — une case constructible. Chaque bâtiment posé en bord de rue
      évinçait donc son mobilier : le défaut n'était pas dans le nettoyage, il était
      dans l'ADRESSE. Les tuiles de route (`Road_Grass`) portent une bande d'herbe
      de 0,31 case de chaque côté de la chaussée (demi-largeur mesurée : 0,19) :
      c'est là que vit le mobilier, décalé de 0,34 vers le bord — DANS sa tuile.
      Une route ne se construit jamais : le mobilier est permanent par construction.
    */
    /** Décalage d'un prop DANS sa case, vers un voisin — relu à la pose du sprite. */
    const edgeShift = new Map<string, { dc: number; dr: number; k: number }>();

    const NATURE = ['tree1', 'tree2', 'tree3', 'bush1', 'rock1', 'rock2', 'potplants'];
    /*
      Décor de PARCELLE : bas, cultivé, sans tronc. Aucun arbre — il masquerait la façade
      qu'on veut mettre en valeur.

      ⚠️ AUCUNE CLÔTURE, et c'est une décision MESURÉE, pas un oubli. Le pack en a trois ;
      les trois ont été rendues, posées, et retirées après les avoir vues à l'écran. Leur
      hauteur naturelle les disqualifie :

        bush1       0,90 case   ← l'échelle d'un décor de parcelle
        potplants   1,28
        flowerbed   1,40
        fence       1,79 case   ← deux fois un buisson
        hedge       1,64        (muret de parc : piliers de brique, grille de fer)
        fenceWood   2,39        (palissade de chantier)

      Posée entre deux maisons, `fence` ne se lit pas comme une limite de propriété mais
      comme un MUR, et `hedge` comme une construction de plus — qu'on croit avoir achetée
      par erreur. La règle 19 tranche : on ne rétrécit pas un asset pour qu'il rentre dans
      une intention, **on choisit un asset dont l'échelle naturelle correspond.** Les
      réduire aurait donné des lattes plus fines que celles du voisin : le défaut exact
      qui a coûté la v0.8.0.

      Les trois restent rendues — elles borderont un parc le jour où il y en aura un.
    */
    const GARDEN = ['bush1', 'flowerbed', 'potplants', 'bush1', 'flowerbed', 'bush1'];
    // Pas de jardinière sur la bande d'herbe : une rangée de plantes de 1,3 m
    // déborde sur la chaussée et se lit comme un buisson planté sur la route
    // (« superposition », Benoit). Le banc et la poubelle, compacts, tiennent.
    const STREET = ['bench', 'bin'];

    this.lightJunctions.clear();

    forEachCellInDrawOrder(this.grid, (cell) => {
      const key = cellKey(cell);
      const noise = Math.abs((Math.sin(cell.col * 45.164 + cell.row * 19.873) * 37193.71) % 1);

      if (network.has(key)) {
        const mask = connectionMask(network, cell.col, cell.row);
        const control = controlFor(mask, cell.col, cell.row);

        /*
          ── Signalisation : au COIN de la case du carrefour ──────────────────
          Le poteau se pose sur le coin de SA tuile (décalage diagonal 0,42 — le coin
          est à 0,5 : il reste sur l'herbe du carrefour). On préfère un coin dont la
          diagonale n'est pas une route, sinon le poteau surplomberait la chaussée
          voisine ; le coin de départ dérive de la position, pour que les feux ne
          s'alignent pas tous du même côté.

          Un STOP regarde la BRANCHE SECONDAIRE — les véhicules qui doivent marquer
          l'arrêt arrivent par elle, et le panneau dit alors la même chose que la
          règle de `core/traffic.ts`. Un feu regarde le centre de son croisement.
        */
        if (control !== null) {
          let corner: readonly [number, number] | null = null;
          for (let i = 0; i < 4; i++) {
            const candidate = DIAGONAL_STEP[(((cell.col + cell.row) % 4) + 4 + i) % 4] ?? [1, 1];
            if (
              !network.has(cellKey({ col: cell.col + candidate[0], row: cell.row + candidate[1] }))
            ) {
              corner = candidate;
              break;
            }
          }

          /*
            ⚠️ AUCUN COIN LIBRE N'EST UNE RAISON DE NE RIEN POSER (ROADMAP 5.3).

            Au cœur d'un quartier dense, les quatre diagonales sont des routes. Cette
            recherche rendait alors `null` et la case sortait sans panneau — mesuré sur
            un pavé de 5 × 5 : **neuf carrefours, zéro signalisation**, pendant que
            `core/traffic.ts` continuait d'appliquer la règle. Le joueur voyait des
            véhicules s'arrêter devant rien.

            On garde donc le coin de départ et on RENTRE le poteau vers le centre de sa
            case : il ne surplombe pas la chaussée voisine, et la règle redevient
            visible. Le placement idéal reste préféré quand il existe.
          */
          const tight = corner === null;
          const spot = corner ?? DIAGONAL_STEP[(((cell.col + cell.row) % 4) + 4) % 4] ?? [1, 1];

          /*
            Même refus, même remède, pour l'ORIENTATION : un STOP sans branche secondaire
            identifiable ne disparaît plus — il regarde le centre de son croisement,
            exactement comme un feu. Mieux vaut un panneau tourné par défaut qu'un
            carrefour muet.
          */
          const facing =
            (control.kind === 'stop' ? minorBranchOf(mask) : null) ?? (spot[1] > 0 ? 0 : 2);

          const name = control.kind === 'light' ? 'trafficLight' : 'stopSign';
          edgeShift.set(key, {
            dc: spot[0],
            dr: spot[1],
            k: tight ? SIGNAL_CORNER_TIGHT : SIGNAL_CORNER,
          });
          wanted.set(key, orientedSprite(name, normalizeFacing(facing)));
          // Un feu AFFICHE son état (`updateTrafficLights`) : il règle SA propre case.
          if (name === 'trafficLight') {
            this.lightJunctions.set(key, { col: cell.col, row: cell.row });
          }
          return;
        }

        /*
          ── Lampadaires en cadence, mobilier occasionnel entre deux ──────────
          Une case de rue sur `LAMP_SPACING` porte son mât ; quelques bancs et
          poubelles se glissent entre. Le côté évite l'axe de la chaussée : premier
          voisin NON route en tournant depuis un départ dérivé de la position — sur
          une avenue droite, le mobilier alterne donc naturellement de côté.
          Déterministe : deux chargements donnent le même éclairage.
        */
        /*
          DÉGAGEMENT autour des carrefours : pas de mât ni de banc sur une case de
          rue voisine d'un croisement — la signalisation y vit déjà, et un poteau
          collé au feu se lisait comme une superposition (capture de Benoit).
        */
        for (const [dc, dr] of SIDE_STEP) {
          const nc = cell.col + dc;
          const nr = cell.row + dr;
          if (!network.has(cellKey({ col: nc, row: nr }))) continue;
          if (controlFor(connectionMask(network, nc, nr), nc, nr) !== null) return;
        }

        const lamp = (((cell.col + cell.row) % LAMP_SPACING) + LAMP_SPACING) % LAMP_SPACING === 0;
        if (!lamp && noise > 0.24) return;

        let side: number | null = null;
        for (let i = 0; i < 4; i++) {
          const candidate = (((cell.col + cell.row) % 4) + 4 + i) % 4;
          const [dc, dr] = SIDE_STEP[candidate] ?? [0, -1];
          if (!network.has(cellKey({ col: cell.col + dc, row: cell.row + dr }))) {
            side = candidate;
            break;
          }
        }
        if (side === null) return;

        const step = SIDE_STEP[side] ?? [0, -1];
        const pick = lamp
          ? 'streetLight'
          : (STREET[Math.floor(noise * 4.545 * STREET.length) % STREET.length] ?? 'bench');
        edgeShift.set(key, { dc: step[0], dr: step[1], k: lamp ? LAMP_EDGE : FURNITURE_EDGE });
        /*
          Le mobilier FAIT FACE à la chaussée : dos au bord, regard vers la rue —
          dans le repère de la VUE, et par la loi de `facing.ts` (l'indice de rotation
          n'est pas l'indice de la direction visée). `orientedSprite` rend le nom nu
          pour un décor sans orientation lisible — une poubelle, un rocher.
        */
        wanted.set(key, orientedSprite(pick, normalizeFacing((side + 2) % 4)));
        return;
      }

      if (occupied.has(key)) return;

      /*
        ══ UNE CASE ENTRE DEUX BÂTIMENTS EST UN JARDIN, PAS UN CHAMP ═══════════

        « Qu'il n'y ait plus de trou entre chaque bâtiment » (Benoit, 21 août). Le trou
        n'était pas un défaut de cadrage des sprites : c'était de l'HERBE NUE, parce que
        la campagne ne décore qu'une case sur cinq et qu'un quartier dense tombe
        statistiquement sur les quatre autres.

        Une case libre cernée de constructions ne se lit pas comme un pré : elle se lit
        comme un vide. On la traite donc comme une parcelle — haie, massif, plantes —
        avec une densité qui SUIT le voisinage bâti au lieu de l'ignorer.

        ⚠️ Le décor de jardin est bas et étroit (`heightRatio` 0,8 ou moins). Y mettre un
        arbre de campagne masquerait la façade du bâtiment voisin, qui est précisément ce
        qu'on cherche à mettre en valeur : combler un vide ne doit pas cacher ce qui le
        borde.
      */
      const neighbours = built === undefined ? 0 : countBuiltNeighbours(built, cell);
      /*
        ⚠️ Le seuil était à DEUX voisins bâtis. Mesuré à l'écran sur une ville de vingt-huit
        bâtiments : deux jardins posés en tout. Une case cernée sur deux côtés est rare —
        la plupart des vides d'un quartier ne touchent qu'un seul bâtiment, et ce sont eux
        qu'on voit.

        Un seul voisin suffit donc, et la densité se gradue : un quartier dense se remplit,
        un bord de ville s'effiloche vers la campagne au lieu de s'arrêter net.
      */
      if (neighbours >= 2 ? noise <= 0.9 : neighbours === 1 && noise <= 0.72) {
        wanted.set(
          key,
          GARDEN[Math.floor(noise * 7.31 * GARDEN.length) % GARDEN.length] ?? 'bush1',
        );
        return;
      }

      // Campagne : une case sur cinq, dispersée — au-delà, la forêt masque le
      // terrain constructible.
      if (noise > 0.22) return;
      wanted.set(key, NATURE[Math.floor(noise * 4.545 * NATURE.length) % NATURE.length] ?? 'tree1');
    });

    for (const [key, sprite] of this.propSprites) {
      if (!wanted.has(key)) {
        this.tileLayer.removeChild(sprite);
        sprite.destroy();
        this.propSprites.delete(key);
        this.propNames.delete(key);
      }
    }

    for (const [key, name] of wanted) {
      const [col, row] = key.split(':').map(Number);
      const cell = { col: col ?? 0, row: row ?? 0 };
      const position = cellToWorld(cell, SYNTY_TILE);
      // Décentré dans sa case : un arbre pile au milieu de chaque losange
      // reconstituerait le quadrillage qu'on cherche à masquer. Le décalage est
      // déterministe et calculé sur les coordonnées d'ÉTAT — un arbre ne doit pas
      // sauter dans sa case au pivot, il doit juste suivre sa case.
      const jitter = ((col ?? 0) * 31 + (row ?? 0) * 17) % 7;

      /*
        Le mobilier urbain se COLLE au bord de sa tuile de route ; la nature se
        disperse.

        Le déplacement se fait en coordonnées ÉCRAN parce que la projection est
        isométrique : un pas d'une demi-case vers le nord vaut (−256, −145) en monde,
        pas (0, −1). On dérive donc le vecteur de la même projection que tout le reste
        plutôt que de le coder en dur. La fraction `k` reste sous 0,5 : le prop ne
        franchit JAMAIS la frontière de sa case — c'est ce qui le rend permanent.
      */
      const shift = edgeShift.get(key);
      let x = position.x;
      let y = position.y;

      let depthCell = cell;

      if (shift === undefined) {
        // Nature : dispersion, pour que la campagne ne redessine pas le quadrillage.
        x += (jitter - 3) * 18;
        y += (jitter - 3) * 9;
      } else {
        const towards = { col: (col ?? 0) + shift.dc, row: (row ?? 0) + shift.dr };
        const target = cellToWorld(towards, SYNTY_TILE);
        x += (target.x - position.x) * shift.k;
        y += (target.y - position.y) * shift.k;
        // Léger décalage LATÉRAL, pour que deux bancs voisins ne s'alignent pas au cordeau.
        x += (jitter - 3) * 4;

        // Un décor se trie où il SE TIENT : position réelle, en cases fractionnaires —
        // l'ordre de rendu suit la géométrie, pas la comptabilité des cases.
        depthCell = {
          col: cell.col + (towards.col - cell.col) * shift.k,
          row: cell.row + (towards.row - cell.row) * shift.k,
        };
      }
      const depth = depthOf(depthCell) * 2 + 0.9;

      /*
        Un prop DÉJÀ posé se repositionne, il ne se saute pas.

        La branche disait `if (has(key)) continue;` : après un pivot, arbres et rochers
        restaient plantés à leur ancienne place pendant que le sol tournait sous eux
        (mesuré : 0 prop déplacé sur 18). Même défaut, même correctif que les routes.
      */
      /*
        Un décor déjà posé se repositionne — mais il change aussi de MODÈLE si le
        contexte de sa case a changé.

        Cette branche ne remplaçait que la position : une case qui passait de
        « campagne » à « bord de rue » — parce qu'un bâtiment voisin venait de tracer
        une route — gardait son arbre. Résultat à l'écran : des arbres plantés le long
        du bitume là où des lampadaires étaient attendus, et l'impression qu'ils
        poussent sur la chaussée. Défaut introduit en 0.6.1 avec le repositionnement au
        pivot.

        Le nom voulu porte déjà le modèle ET sa rotation : le comparer suffit.
      */
      const existing = this.propSprites.get(key);
      if (existing !== undefined && !existing.destroyed) {
        if (this.propNames.get(key) === name) {
          existing.position.set(x, y);
          existing.zIndex = depth;
          continue;
        }
        // Modèle différent : on repart d'un sprite neuf plutôt que de bricoler celui-ci.
        this.tileLayer.removeChild(existing);
        existing.destroy();
        this.propSprites.delete(key);
        this.propNames.delete(key);
      }

      let texture = this.propTextures.get(name);
      if (texture === undefined) {
        texture = await cityTexture(name);
        if (generation !== this.propsGeneration) return;
        this.propTextures.set(name, texture);
      }

      const sprite = new Sprite(texture);
      sprite.anchor.set(0.5, 0.5);
      sprite.position.set(x, y);
      sprite.scale.set(assetScale(name) ?? 0.72);
      sprite.zIndex = depth;

      this.propSprites.set(key, sprite);
      this.propNames.set(key, name);
      this.tileLayer.addChild(sprite);
    }
  }

  /**
   * Colore la lampe de chaque feu selon `greenAxis` — la MÊME fonction, avec la MÊME
   * horloge (`performance.now()`) que celle que les véhicules obéissent dans
   * `traffic.update`. C'est ce qui transforme « une voiture en panne » en « une
   * voiture au rouge » : sans état affiché, chaque attente au feu se lisait comme un
   * blocage (rapport de Benoit, 18 août — aucun blocage réel mesuré par la sonde).
   *
   * La lampe est un ENFANT du sprite du feu : elle suit sa position, son tri et son
   * culling, et disparaît avec lui. Elle montre l'axe nord-sud — vert quand il passe,
   * rouge quand l'axe croisé a le vert, ambre pendant le tout-au-rouge : trois états,
   * exactement ceux du cycle.
   */
  private updateTrafficLights(now: number): void {
    for (const [key, junction] of this.lightJunctions) {
      const sprite = this.propSprites.get(key);
      if (sprite === undefined || sprite.destroyed) continue;

      /*
        Le feu a TROIS AMPOULES, à trois endroits (Benoit, capture du 18 août) :
        rouge en haut, ambre au milieu, verte en bas — peintes dans le modèle et
        MESURÉES rotation par rotation (`npm run measure:lamps`, règle 14 : le rouge
        et le vert sont les seules teintes non ambiguës de l'asset, leur centroïde
        est l'ampoule). On allume LA BONNE, au lieu d'un disque unique qui changeait
        de couleur au-dessus du boîtier.
      */
      const spots = TRAFFIC_BULBS[this.propNames.get(key) ?? ''];
      if (spots === undefined) continue;

      let box = sprite.children[0] as Container | undefined;
      if (box === undefined) {
        box = new Container();
        for (const [color, at] of [
          [0xff5a5a, spots.red],
          [0xffb400, spots.amber],
          [0x5ed88c, spots.green],
        ] as const) {
          const bulb = new Graphics();
          bulb.circle(0, 0, 46).fill({ color, alpha: 0.35 });
          bulb.circle(0, 0, 22).fill({ color, alpha: 1 });
          bulb.position.set(at[0], at[1]);
          /*
            Fusion ADDITIVE : la teinte de nuit du calque multiplie tous ses enfants
            (Pixi v8) — en fusion normale, le rouge devenait terne au plus noir de
            la nuit. L'additif ajoute au fond : une lampe reste une lampe, de jour
            comme de nuit.
          */
          bulb.blendMode = 'add';
          bulb.alpha = 0;
          box.addChild(bulb);
        }
        sprite.addChild(box);
      }

      // L'ampoule affichée montre l'état de l'axe nord-sud — exactement ce que les
      // véhicules obéissent : verte quand il passe, rouge quand l'axe croisé a le
      // vert, ambre pendant le tout-au-rouge.
      const green = greenAxis(junction.col, junction.row, now);
      const [red, amber, verte] = box.children;
      if (red !== undefined) red.alpha = green === 'eo' ? 1 : 0;
      if (amber !== undefined) amber.alpha = green === null ? 1 : 0;
      if (verte !== undefined) verte.alpha = green === 'ns' ? 1 : 0;
    }
  }

  /** Halos des lampadaires — objets AUTONOMES du calque, triés SOUS leur mât. */
  private readonly lampHalos = new Map<string, Graphics>();

  /**
   * Les lampadaires ÉCLAIRENT (demande de Benoit, 18 août — « aucun éclairage pour
   * les lampadaires », puis « il faut qu'il soit dessous le png, pas par-dessus »).
   *
   * Un halo chaud en fusion ADDITIVE au DIFFUSEUR de chaque mât — la position est
   * MESURÉE (`npm run measure:lamps`) : la lanterne pend au bout d'un bras, pas au
   * sommet du poteau. Le halo est un objet autonome du calque, trié juste SOUS le
   * sprite du mât : en enfant Pixi il passait toujours devant son parent, et le
   * cœur blanc effaçait la lanterne au lieu de rayonner derrière elle.
   */
  private applyStreetLamps(stars: number): void {
    // La courbe de la direction artistique : plein feu au crépuscule et à l'aube,
    // tamisé la nuit, rien le jour (`urbanLightAt`).
    const alpha = urbanLightAt(stars);
    const seen = new Set<string>();

    for (const [key, name] of this.propNames) {
      if (!name.startsWith('streetLight')) continue;
      const sprite = this.propSprites.get(key);
      if (sprite === undefined || sprite.destroyed) continue;
      seen.add(key);

      let halo = this.lampHalos.get(key);
      if (halo === undefined || halo.destroyed) {
        halo = new Graphics();
        // Grand halo + cœur, en pixels MONDE (l'objet n'hérite plus de l'échelle
        // du mât) : la lanterne baigne son bout de trottoir.
        halo.circle(0, 0, 190).fill({ color: 0xffc978, alpha: 0.18 });
        halo.circle(0, 0, 90).fill({ color: 0xffd9a0, alpha: 0.42 });
        halo.circle(0, 0, 34).fill({ color: 0xffefd0, alpha: 0.9 });
        halo.blendMode = 'add';
        this.lampHalos.set(key, halo);
        this.tileLayer.addChild(halo);
      }

      // Position monde du diffuseur : celle du mât + l'offset mesuré À SON échelle.
      const head = LAMP_HEADS[name] ?? [0, -sprite.texture.height * 0.34];
      const s = sprite.scale.x;
      halo.position.set(sprite.x + head[0] * s, sprite.y + head[1] * s);
      // Juste SOUS le mât : la lumière rayonne derrière la lanterne, qui reste nette.
      halo.zIndex = sprite.zIndex - 0.01;
      halo.alpha = alpha;
    }

    // Un mât disparu (bâtiment, pivot, vente de route) emporte son halo.
    for (const [key, halo] of this.lampHalos) {
      if (seen.has(key)) continue;
      this.tileLayer.removeChild(halo);
      if (!halo.destroyed) halo.destroy();
      this.lampHalos.delete(key);
    }
  }

  /* ── Culling ───────────────────────────────────────────────────────── */

  /**
   * Marge de sécurité, en pixels monde.
   *
   * Petite, parce que l'étendue réelle de chaque sprite est calculée exactement.
   * Première version : une marge forfaitaire de six hauteurs de tuile, censée couvrir
   * les toits les plus hauts. Elle doublait la zone considérée comme visible — le
   * culling ne cullait presque rien et coûtait 1 fps net. Mesuré, pas supposé.
   */
  private static readonly CULL_MARGIN = 48;

  private lastCullKey = '';
  private culledCount = 0;

  /**
   * Masque ce qui sort du champ.
   *
   * PixiJS ne cull pas tout seul : chaque sprite hors écran passe quand même par la
   * transformation et le lot de rendu. Sur une grande grille, la quasi-totalité du
   * travail concerne des tuiles que personne ne voit.
   *
   * Recalculé seulement quand la caméra a bougé : parcourir tous les sprites à chaque
   * frame pour un résultat identique serait précisément le gaspillage qu'on corrige.
   */
  private updateCulling(): void {
    const camera = this.camera;
    if (camera === null) return;

    const { x, y, zoom } = camera.getState();
    const key = `${x.toFixed(1)}:${y.toFixed(1)}:${zoom.toFixed(4)}:${String(this.tileLayer.children.length)}`;
    if (key === this.lastCullKey) return;
    this.lastCullKey = key;

    const halfWidth = this.viewport.width / (2 * zoom) + CityRenderer.CULL_MARGIN;
    const halfHeight = this.viewport.height / (2 * zoom) + CityRenderer.CULL_MARGIN;
    const minX = x - halfWidth;
    const maxX = x + halfWidth;
    const minY = y - halfHeight;
    const maxY = y + halfHeight;

    let culled = 0;
    for (const child of this.tileLayer.children) {
      // Ne culler QUE les sprites. Le calque héberge aussi des `Graphics` — le fantôme
      // de placement et l'éclat de pose — dont la visibilité appartient à l'animation
      // qui les pilote. Un `as Sprite` masquait ce mélange : le compilateur voyait des
      // sprites partout, le runtime lisait `undefined`, et le culling décidait de la
      // visibilité d'objets qui ne lui appartiennent pas.
      if (!(child instanceof Sprite)) continue;

      /*
        ⚠️ LE FANTÔME N'APPARTIENT PAS AU CULLING.

        Sa visibilité est pilotée par `moveGhostTo` : masqué tant qu'aucune case n'est
        survolée, masqué quand le curseur sort de la grille. Mais le fantôme EST un
        `Sprite` de ce calque, donc cette boucle écrivait `visible = true` dessus au
        premier mouvement de caméra — il ressuscitait à sa dernière position connue, et
        l'aperçu d'un bâtiment surgissait tout seul au milieu de la ville.

        Mesuré : `moveGhostTo(null)` puis un `zoomBy(1.05)` suffisait à le rendre visible.
        Le défaut est invisible sur une capture et évident dès le deuxième geste — encore
        la règle 18.

        L'exclusion est explicite plutôt qu'un drapeau `renderable` : elle nomme le
        propriétaire de la décision, ce qui est précisément ce qui manquait.
      */
      if (child === this.ghost) continue;

      // Étendue RÉELLE du sprite, pas sa seule position : un bâtiment est ancré près
      // de sa base et déborde de plusieurs centaines de pixels vers le haut. Tester la
      // position seule ferait disparaître les toits avant qu'ils ne sortent du champ.
      const { width, height } = child.texture;
      const visible =
        child.x + (1 - child.anchor.x) * width >= minX &&
        child.x - child.anchor.x * width <= maxX &&
        child.y + (1 - child.anchor.y) * height >= minY &&
        child.y - child.anchor.y * height <= maxY;

      child.visible = visible;
      if (!visible) culled++;
    }

    this.culledCount = culled;
  }

  getCulledCount(): number {
    return this.culledCount;
  }

  private measureViewport(): { width: number; height: number } {
    const rect = this.options.container.getBoundingClientRect();
    return {
      width: Math.max(1, Math.round(rect.width)),
      height: Math.max(1, Math.round(rect.height)),
    };
  }

  private readonly onResize = (): void => {
    const next = this.measureViewport();
    if (next.width === this.viewport.width && next.height === this.viewport.height) {
      return;
    }

    this.viewport = next;
    this.app.renderer.resize(next.width, next.height);
    this.camera?.setViewport(next);
  };

  private readonly onFrame = (): void => {
    const camera = this.camera;
    if (camera === null) {
      return;
    }

    const now = performance.now();
    const deltaMs = this.lastFrameTime === 0 ? 0 : now - this.lastFrameTime;
    this.lastFrameTime = now;

    this.quality.frame(now);
    this.tweens.update(now);
    camera.update(deltaMs);

    // Les feux montrent leur couleur — la même horloge que `traffic.update`, donc ce
    // que le joueur voit est ce que les véhicules obéissent.
    this.updateTrafficLights(now);

    // Les pastilles de palier se fondent avec le recul (3.4).
    this.updateLevelPips();

    /*
      Passe d'ÉCLAIRAGE VIVANT, à 4 Hz — pas par frame, pas seulement quand la nuit
      bouge. Les fenêtres s'allument et s'éteignent au fil du temps (`windowLitGate`)
      et les lampadaires suivent le crépuscule : les deux dépendent de l'horloge,
      pas du seul niveau de nuit — le garde à 1 % de `applyNightLighting` ne
      suffisait donc plus à les tenir à jour.
    */
    if (this.lastNight >= 0 && now - this.lastLightingPass > 250) {
      this.lastLightingPass = now;
      this.applyWindowGlow(this.lastNight);
      this.applyStreetLamps(this.lastNight);
    }

    // Horloge ABSOLUE, pas `performance.now()` : un onglet masqué vingt minutes doit
    // retrouver le bon moment du jour en revenant, pas reprendre où il s'était arrêté.
    this.sky?.update(this.worldClock());
    // Le cycle des feux est une fonction PURE du temps : il reçoit l'horloge au lieu de
    // la lire, donc il se teste sans navigateur et ne dérive pas quand l'onglet est masqué
    // et que les frames s'espacent de plusieurs secondes.
    this.traffic?.update(deltaMs, now);
    this.applyNightLighting(this.worldClock());
    this.updatePlateShadow();

    const { x, y, zoom } = camera.getState();

    // Creux de zoom et fondu pendant un pivot de vue : appliqués ICI et non dans le
    // tween, parce que cette ligne écrase l'échelle à chaque frame.
    this.world.scale.set(zoom);
    this.world.position.set(
      this.viewport.width / 2 - x * zoom,
      this.viewport.height / 2 - y * zoom,
    );

    this.updateCulling();
  };
}

function cellKey(cell: GridCell): string {
  return `${String(cell.col)}:${String(cell.row)}`;
}

export { createGrid, cellCount };
